import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../../../shared/api/apiClient";
import CrmJourneyTimeline from "../../components/CrmJourneyTimeline";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./ActivitiesPage.css";

const ACTIVITY_TYPES = ["email", "call", "message", "meeting", "note"];
const CHANNELS = ["email", "whatsapp", "sms", "call", "meeting"];
const DIRECTIONS = ["in", "out"];
const STATUSES = ["planned", "done", "canceled"];

const ACTIVITY_PREFIX = "activity:";
const COLUMN_PREFIX = "activity-column:";
const activityKey = (activityUiId) =>
  `${ACTIVITY_PREFIX}${String(activityUiId)}`;
const columnKey = (columnId) => `${COLUMN_PREFIX}${String(columnId)}`;
const parseColumnId = (value) => String(value || "").replace(COLUMN_PREFIX, "");

const getActivityColumns = (t) => [
  {
    id: "overdue",
    title: t("crm_activities_column_overdue", { defaultValue: "Overdue" }),
    subtitle: t("crm_activities_column_overdue_subtitle", {
      defaultValue: "These actions should be handled first.",
    }),
    tone: "danger",
  },
  {
    id: "today",
    title: t("crm_activities_column_today", { defaultValue: "Today" }),
    subtitle: t("crm_activities_column_today_subtitle", {
      defaultValue: "Your most important actions for today.",
    }),
    tone: "primary",
  },
  {
    id: "upcoming",
    title: t("crm_activities_column_upcoming", { defaultValue: "Upcoming" }),
    subtitle: t("crm_activities_column_upcoming_subtitle", {
      defaultValue: "Planned future work.",
    }),
    tone: "",
  },
  {
    id: "noDate",
    title: t("crm_activities_column_no_date", { defaultValue: "No date" }),
    subtitle: t("crm_activities_column_no_date_subtitle", {
      defaultValue: "Actions without a scheduled time.",
    }),
    tone: "",
  },
  {
    id: "done",
    title: t("crm_activities_column_done", { defaultValue: "Done" }),
    subtitle: t("crm_activities_column_done_subtitle", {
      defaultValue: "Completed history.",
    }),
    tone: "success",
  },
  {
    id: "canceled",
    title: t("crm_activities_column_canceled", { defaultValue: "Canceled" }),
    subtitle: t("crm_activities_column_canceled_subtitle", {
      defaultValue: "Canceled actions.",
    }),
    tone: "warning",
  },
];

const emptyForm = {
  type: "note",
  subject: "",
  content: "",
  when_at: "",
  channel: "",
  direction: "",
  status: "planned",
  contact_ui_id: "",
  lead_ui_id: "",
  deal_ui_id: "",
};

const defaultColumnFilters = {
  ui_id: "",
  subject: "",
  type: "",
  status: "",
  channel: "",
  direction: "",
  contact_ui_id: "",
  lead_ui_id: "",
  deal_ui_id: "",
  owner_ui_id: "",
  when_at: "",
  created_at: "",
};

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).toLowerCase();

const normalizeItems = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const mapTaskToWorkActivity = (task) => {
  const rawStatus = String(task?.status || "pending").toLowerCase();
  const isDone = rawStatus === "done" || rawStatus === "completed";
  return {
    ...task,
    ui_id: `task-${task.ui_id ?? task.id}`,
    __source: "task",
    __task_id: task.id,
    __task_ui_id: task.ui_id,
    type: "note",
    subject: task.title || `Task #${task.ui_id ?? task.id}`,
    content: task.description || "",
    when_at: task.due_at || null,
    status: isDone ? "done" : "planned",
    channel: null,
    direction: null,
    outcome: isDone ? "completed" : null,
    owner_ui_id: task.owner_ui_id || null,
    owner_display_name: task.owner_display_name || null,
    owner_email: task.owner_email || null,
    contact_ui_id: task.contact_ui_id || null,
    contact_display_name: task.contact_display_name || null,
    contact_primary_phone: task.contact_primary_phone || null,
    contact_primary_email: task.contact_primary_email || null,
    lead_ui_id: task.lead_ui_id || null,
    lead_title: task.lead_title || null,
    deal_ui_id: task.deal_ui_id || null,
    deal_title: task.deal_title || null,
    created_at: task.created_at || null,
  };
};

const isTaskWorkItem = (item) => item?.__source === "task" && item?.__task_id;

const formatBackendError = (raw, fallback) => {
  const detail = raw?.response?.data?.detail ?? raw?.message ?? raw;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (detail && typeof detail === "object")
    return detail.msg || JSON.stringify(detail);
  return fallback;
};

const formatDateTime = (value, t) => {
  if (!value) return t("crm_activities_no_date", { defaultValue: "No date" });
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const formatDateOnly = (value, t) => {
  if (!value) return t("crm_activities_no_date", { defaultValue: "No date" });
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toBackendDateTimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const makeTodayAtNine = () => {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  return toBackendDateTimeLocal(d);
};

const makeTomorrowAtNine = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toBackendDateTimeLocal(d);
};

const padDatePart = (n) => String(n).padStart(2, "0");

const toDateInputValue = (date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;

const toTimeInputValue = (value, fallback = "09:00") => {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return `${padDatePart(d.getHours())}:${padDatePart(d.getMinutes())}`;
};

const combineDateAndTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const [year, month, day] = String(dateValue).split("-").map(Number);
  const [hours, minutes] = String(timeValue || "09:00")
    .split(":")
    .map(Number);
  if (!year || !month || !day) return null;
  const d = new Date(
    year,
    month - 1,
    day,
    Number.isFinite(hours) ? hours : 9,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0,
  );
  return toBackendDateTimeLocal(d);
};

const getTodayDateInputValue = () => toDateInputValue(new Date());

const getTomorrowDateInputValue = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toDateInputValue(d);
};

const DEFAULT_WORK_SCHEDULE = {
  enabled: false,
  start_time: "09:00",
  end_time: "18:00",
  slot_minutes: 15,
  min_ahead_minutes: 15,
};

const TASK_SLOT_MINUTES = 15;
const MIN_SCHEDULE_AHEAD_MINUTES = 15;

const normalizeWorkSchedule = (raw) => {
  const cfg = raw && typeof raw === "object" ? raw : {};
  const slot = Number(cfg.slot_minutes ?? DEFAULT_WORK_SCHEDULE.slot_minutes);
  const minAhead = Number(
    cfg.min_ahead_minutes ?? DEFAULT_WORK_SCHEDULE.min_ahead_minutes,
  );
  return {
    enabled: !!cfg.enabled,
    start_time: String(
      cfg.start_time || DEFAULT_WORK_SCHEDULE.start_time,
    ).slice(0, 5),
    end_time: String(cfg.end_time || DEFAULT_WORK_SCHEDULE.end_time).slice(
      0,
      5,
    ),
    slot_minutes: [5, 10, 15, 30, 60].includes(slot)
      ? slot
      : DEFAULT_WORK_SCHEDULE.slot_minutes,
    min_ahead_minutes: [0, 5, 10, 15, 30, 60].includes(minAhead)
      ? minAhead
      : DEFAULT_WORK_SCHEDULE.min_ahead_minutes,
  };
};

const timeToMinutes = (value) => {
  const [h, m] = String(value || "00:00")
    .split(":")
    .map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const minutesToTime = (minutes) => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${padDatePart(h)}:${padDatePart(m)}`;
};

const makeTimeSlots = (schedule = DEFAULT_WORK_SCHEDULE) => {
  const cfg = normalizeWorkSchedule(schedule);
  const step = cfg.slot_minutes || TASK_SLOT_MINUTES;
  const start = cfg.enabled ? timeToMinutes(cfg.start_time) : 0;
  const end = cfg.enabled ? timeToMinutes(cfg.end_time) : 24 * 60;
  const slots = [];

  if (cfg.enabled && end <= start) {
    // Cross-midnight schedules are treated as a full-day schedule in this UI.
    for (let m = 0; m < 24 * 60; m += step) slots.push(minutesToTime(m));
    return slots;
  }

  for (let m = start; m < end; m += step) {
    slots.push(minutesToTime(m));
  }
  return slots;
};

const getDateInputFromValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return toDateInputValue(d);
};

const getTimeInputFromValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${padDatePart(d.getHours())}:${padDatePart(d.getMinutes())}`;
};

const ceilDateToSlot = (date, stepMinutes = TASK_SLOT_MINUTES) => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const rounded = Math.ceil(minutes / stepMinutes) * stepMinutes;
  if (rounded >= 60) {
    d.setHours(d.getHours() + 1, 0, 0, 0);
  } else {
    d.setMinutes(rounded, 0, 0);
  }
  return d;
};

const ceilToSlot = (date, stepMinutes = TASK_SLOT_MINUTES) => {
  const d = ceilDateToSlot(date, stepMinutes);
  return `${padDatePart(d.getHours())}:${padDatePart(d.getMinutes())}`;
};

const addMinutes = (date, minutes) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() + minutes);
  return d;
};

const getMinimumPlanningDateTime = (schedule = DEFAULT_WORK_SCHEDULE) => {
  const cfg = normalizeWorkSchedule(schedule);
  return ceilDateToSlot(
    addMinutes(new Date(), cfg.min_ahead_minutes ?? MIN_SCHEDULE_AHEAD_MINUTES),
    cfg.slot_minutes || TASK_SLOT_MINUTES,
  );
};

const getMinimumPlanningSlotForDate = (
  dateValue,
  schedule = DEFAULT_WORK_SCHEDULE,
) => {
  if (!dateValue) return null;
  const cfg = normalizeWorkSchedule(schedule);
  if (dateValue !== getTodayDateInputValue()) {
    return cfg.enabled ? cfg.start_time : "00:00";
  }
  const minDate = getMinimumPlanningDateTime(cfg);
  return `${padDatePart(minDate.getHours())}:${padDatePart(minDate.getMinutes())}`;
};

const isPastPlanningSlot = (
  dateValue,
  timeValue,
  schedule = DEFAULT_WORK_SCHEDULE,
) => {
  if (!dateValue || !timeValue) return false;
  if (dateValue !== getTodayDateInputValue()) return false;
  const minimumSlot = getMinimumPlanningSlotForDate(dateValue, schedule);
  return Boolean(minimumSlot && timeValue < minimumSlot);
};

const getAvailableTimeSlotsForDate = (
  dateValue,
  schedule = DEFAULT_WORK_SCHEDULE,
) => {
  const slots = makeTimeSlots(schedule);
  const minSlot = getMinimumPlanningSlotForDate(dateValue, schedule);
  if (!minSlot) return slots;
  return slots.filter((slot) => slot >= minSlot);
};

const getBusyTaskSlots = (items, dateValue, excludeUiId) => {
  const busy = new Set();
  if (!dateValue) return busy;
  items.forEach((item) => {
    if (!item?.when_at) return;
    if (item.ui_id === excludeUiId) return;
    if (item.status === "done" || item.status === "canceled") return;
    if (getDateInputFromValue(item.when_at) !== dateValue) return;
    const slot = getTimeInputFromValue(item.when_at);
    if (slot) busy.add(slot);
  });
  return busy;
};

const getFirstFreeSlot = (
  items,
  dateValue,
  excludeUiId,
  preferredTime,
  schedule = DEFAULT_WORK_SCHEDULE,
) => {
  const busy = getBusyTaskSlots(items, dateValue, excludeUiId);
  const availableSlots = getAvailableTimeSlotsForDate(dateValue, schedule);
  const minimumSlot = getMinimumPlanningSlotForDate(dateValue, schedule);
  const fallbackSlot = normalizeWorkSchedule(schedule).enabled
    ? normalizeWorkSchedule(schedule).start_time
    : "00:00";
  const preferred =
    preferredTime && availableSlots.includes(preferredTime)
      ? preferredTime
      : minimumSlot || availableSlots[0] || fallbackSlot;

  const ordered = [
    preferred,
    ...availableSlots.filter((slot) => slot >= preferred),
    ...availableSlots.filter((slot) => slot < preferred),
  ].filter((slot, idx, arr) => arr.indexOf(slot) === idx);

  return (
    ordered.find((slot) => !busy.has(slot)) ||
    availableSlots.find((slot) => !busy.has(slot)) ||
    preferred
  );
};

const makeYesterdayAtNine = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(9, 0, 0, 0);
  return toBackendDateTimeLocal(d);
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isFutureDay = (date, today) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d > t;
};

const getActivityIcon = (type) => {
  switch (type) {
    case "email":
      return <i className="lni lni-envelope-1" />;
    case "call":
      return <i className="lni lni-telephone-1" />;
    case "message":
      return <i className="lni lni-comment-1-text" />;
    case "meeting":
      return <i className="lni lni-google-meet" />;
    case "note":
    default:
      return <i className="lni lni-notebook-1" />;
  }
};

const getActivityTitle = (activity, t) => {
  if (activity.subject) return activity.subject;
  switch (activity.type) {
    case "email":
      return t("crm_activities_email_activity", {
        defaultValue: "Email activity",
      });
    case "call":
      return t("crm_activities_call_activity", {
        defaultValue: "Call activity",
      });
    case "message":
      return t("crm_activities_message_activity", {
        defaultValue: "Message activity",
      });
    case "meeting":
      return t("crm_activities_meeting", { defaultValue: "Meeting" });
    case "note":
    default:
      return t("crm_activities_note", { defaultValue: "Note" });
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case "done":
      return t("crm_activities_status_done", { defaultValue: "Done" });
    case "canceled":
      return t("crm_activities_status_canceled", { defaultValue: "Canceled" });
    case "planned":
    default:
      return t("crm_activities_status_planned", { defaultValue: "Planned" });
  }
};

const getTypeLabel = (type, t) =>
  t(`crm_activities_type_${type}`, { defaultValue: type });

const getChannelLabel = (channel, t) =>
  t(`crm_activities_channel_${channel}`, { defaultValue: channel });

const getDirectionLabel = (direction, t) => {
  if (direction === "in")
    return t("crm_activities_direction_incoming", { defaultValue: "incoming" });
  if (direction === "out")
    return t("crm_activities_direction_outgoing", { defaultValue: "outgoing" });
  return "—";
};

const getActivityColumnId = (activity) => {
  if (activity.status === "done") return "done";
  if (activity.status === "canceled") return "canceled";
  if (!activity.when_at) return "noDate";

  const now = new Date();
  const d = new Date(activity.when_at);

  if (Number.isNaN(d.getTime())) {
    return "noDate";
  }

  // ЛЮБОЕ время в прошлом = просрочено
  if (d.getTime() < now.getTime()) {
    return "overdue";
  }

  if (isSameDay(d, now)) {
    return "today";
  }

  if (isFutureDay(d, now)) {
    return "upcoming";
  }

  return "today";
};

const sortRows = (rows, sortConfig) => {
  if (!sortConfig?.key) return rows;
  const direction = sortConfig.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a?.[sortConfig.key];
    const bv = b?.[sortConfig.key];
    const left =
      sortConfig.key.includes("_at") && av ? new Date(av).getTime() : av;
    const right =
      sortConfig.key.includes("_at") && bv ? new Date(bv).getTime() : bv;
    if (left === null || left === undefined || left === "") return 1;
    if (right === null || right === undefined || right === "") return -1;
    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return 0;
  });
};

const getOptionTitleByUiId = (options, uiId) => {
  const value = String(uiId || "").trim();
  if (!value) return "";
  return options.find((o) => String(o.ui_id) === value)?.title || "";
};

function EntitySearchSelect({
  label,
  placeholder,
  options,
  value,
  onChange,
  disabled,
  loading,
  errorText,
  invalid,
}) {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  const selected = useMemo(() => {
    const v = String(value || "").trim();
    if (!v) return null;
    return options.find((o) => String(o.ui_id) === v) || null;
  }, [options, value]);

  const selectedLabel = selected
    ? `${selected.ui_id} — ${selected.title}${selected.subtitle ? ` (${selected.subtitle})` : ""}`
    : "";

  const filtered = useMemo(() => {
    const query = normalize(q).trim();
    if (!query) return options;
    return options.filter((o) =>
      [o.ui_id, o.title, o.subtitle, `${o.ui_id} — ${o.title}`].some((x) =>
        normalize(x).includes(query),
      ),
    );
  }, [options, q]);

  useEffect(() => setActiveIdx(0), [q, open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pick = (uiId) => {
    onChange(uiId ? String(uiId) : "");
    setQ("");
    setOpen(false);
  };

  const onClosedKeyDown = (e) => {
    if (disabled) return;
    if (["Enter", " ", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      openDropdown();
    }
    if (e.key === "Backspace" && value) {
      e.preventDefault();
      pick("");
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[activeIdx];
      if (item) pick(item.ui_id);
    }
  };

  return (
    <div className="activities-select-root" ref={rootRef}>
      <label className="form-label">{label}</label>
      <div
        className={`activities-select-control ${invalid ? "is-invalid" : ""}`}
        role="combobox"
        aria-expanded={open ? "true" : "false"}
        aria-invalid={invalid ? "true" : "false"}
        tabIndex={disabled ? -1 : 0}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        onKeyDown={onClosedKeyDown}
        title={selectedLabel}
      >
        <span
          className={
            selected
              ? "activities-select-value"
              : "activities-select-placeholder"
          }
        >
          {selected ? selectedLabel : placeholder}
        </span>
        <span className="activities-select-actions">
          {value ? (
            <button
              type="button"
              className="activities-select-clear"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                pick("");
              }}
              disabled={disabled}
              title={t("clear", { defaultValue: "Clear" })}
            >
              ×
            </button>
          ) : null}
          <span className="activities-select-chevron">⌄</span>
        </span>
      </div>

      {open && (
        <div
          className="activities-select-menu"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            className="form-control activities-select-search"
            placeholder={t("search", { defaultValue: "Search" }) + "..."}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKeyDown}
            disabled={disabled}
          />
          <div className="activities-select-list">
            {loading ? (
              <div className="activities-select-empty">
                {t("loading", { defaultValue: "Loading" })}...
              </div>
            ) : errorText ? (
              <div className="activities-select-error">{errorText}</div>
            ) : filtered.length === 0 ? (
              <div className="activities-select-empty">
                {t("crm_activities_no_results", { defaultValue: "No results" })}
              </div>
            ) : (
              filtered.map((item, idx) => {
                const labelText = `${item.ui_id} — ${item.title}${item.subtitle ? ` (${item.subtitle})` : ""}`;
                return (
                  <button
                    key={`${item.ui_id}-${item.title}`}
                    type="button"
                    className={`activities-select-option ${idx === activeIdx ? "active" : ""}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => pick(item.ui_id)}
                    title={labelText}
                  >
                    {labelText}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ActivitiesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const ACTIVITY_COLUMNS = useMemo(() => getActivityColumns(t), [t]);
  const academyQuery = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const academyTaskPlanning =
    academyQuery.get("academy_task_planning") === "1";
  const academyTaskId = academyQuery.get("academy_task_id");
  const academyActivityCreate =
    academyQuery.get("academy") === "1" && academyQuery.get("create") === "1";
  const academyActivityContactUiId = academyQuery.get("contact_ui_id") || "";
  const academyActivityDealUiId = academyQuery.get("deal_ui_id") || "";
  const academyActivityTitle = academyQuery.get("activity_title") || "";
  const academyActivityDescription =
    academyQuery.get("activity_description") || "";

  const [activities, setActivities] = useState([]);
  const [workSchedule, setWorkSchedule] = useState(DEFAULT_WORK_SCHEDULE);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);

  const [loading, setLoading] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [error, setError] = useState("");
  const [lookupsError, setLookupsError] = useState("");
  const [filtersError, setFiltersError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [academySearchGuideVisible, setAcademySearchGuideVisible] =
    useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);
  const [sortConfig, setSortConfig] = useState({
    key: "when_at",
    direction: "asc",
  });

  const [viewMode, setViewMode] = useState("kanban");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [density, setDensity] = useState("compact");
  const [activeMobileColumnId, setActiveMobileColumnId] = useState("overdue");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [detailsActivity, setDetailsActivity] = useState(null);
  const [planningMove, setPlanningMove] = useState(null);

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState("");
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);

  const [containers, setContainers] = useState({});
  const [activeActivityKey, setActiveActivityKey] = useState(null);
  const activeFromContainerRef = useRef(null);
  const academyActivityFormOpenedRef = useRef(false);
  const academySearchSeededTaskRef = useRef("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const contactOptions = useMemo(
    () =>
      contacts
        .map((c) => ({
          ui_id: c.ui_id,
          title:
            c.full_name ||
            t("crm_activities_contact_number", {
              defaultValue: "Contact #{{id}}",
              id: c.ui_id,
            }),
          subtitle:
            c.email ||
            c.main_email ||
            c.emails?.find?.((e) => e.status === "main")?.email ||
            c.emails?.[0]?.email ||
            "",
        }))
        .filter((x) => x.ui_id != null),
    [contacts, t],
  );

  const leadOptions = useMemo(
    () =>
      leads
        .map((l) => ({
          ui_id: l.ui_id,
          title:
            l.title ||
            t("crm_activities_lead_number", {
              defaultValue: "Lead #{{id}}",
              id: l.ui_id,
            }),
          subtitle: l.status || "",
        }))
        .filter((x) => x.ui_id != null),
    [leads, t],
  );

  const dealOptions = useMemo(
    () =>
      deals
        .map((d) => ({
          ui_id: d.ui_id,
          title:
            d.title ||
            t("crm_activities_deal_number", {
              defaultValue: "Deal #{{id}}",
              id: d.ui_id,
            }),
          subtitle: d.status || "",
        }))
        .filter((x) => x.ui_id != null),
    [deals, t],
  );

  const fetchWorkSchedule = async () => {
    try {
      const res = await api.get("/auth/crmSettings");
      setWorkSchedule(
        normalizeWorkSchedule(res?.data?.settings_json?.work_schedule),
      );
    } catch (e) {
      console.warn(
        "[MyWork] Failed to load work schedule, using 24h schedule",
        e,
      );
      setWorkSchedule(DEFAULT_WORK_SCHEDULE);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError("");
      // My Work / Activities board is now a VIEW over Tasks.
      // Activity records remain timeline/history events, but this board must show actionable tasks.
      const res = await api.get("/tasks", { params: { skip: 0, limit: 1000 } });
      setActivities(normalizeItems(res.data).map(mapTaskToWorkActivity));
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("crm_activities_load_error", {
            defaultValue: "Failed to load work tasks",
          }),
        ),
      );
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      setLookupsLoading(true);
      setLookupsError("");
      const [contactsRes, leadsRes, dealsRes] = await Promise.all([
        api.get("/contacts", { params: { skip: 0, limit: 1000 } }),
        api.get("/leads", { params: { skip: 0, limit: 1000 } }),
        api.get("/deals", { params: { skip: 0, limit: 1000 } }),
      ]);
      setContacts(normalizeItems(contactsRes.data));
      setLeads(normalizeItems(leadsRes.data));
      setDeals(normalizeItems(dealsRes.data));
    } catch (e) {
      console.error(e);
      setLookupsError(
        formatBackendError(
          e,
          t("crm_activities_dropdown_load_error", {
            defaultValue: "Failed to load dropdown data",
          }),
        ),
      );
    } finally {
      setLookupsLoading(false);
    }
  };

  const applySavedFilter = (filter) => {
    if (!filter) return;
    const f = filter.filters || {};
    setSearchQuery(f.searchQuery || "");
    setStatusFilter(f.statusFilter || "active");
    setTypeFilter(f.typeFilter || "");
    setChannelFilter(f.channelFilter || "");
    setColumnFilters({ ...defaultColumnFilters, ...(f.columnFilters || {}) });
    setSortConfig({
      key: filter.sort_by || f.sortConfig?.key || "when_at",
      direction: filter.sort_dir || f.sortConfig?.direction || "asc",
    });
    setRowsPerPage(filter.per_page || f.rowsPerPage || 10);
    setCurrentPage(1);
    setViewMode(f.viewMode === "table" ? "table" : "kanban");
    setSelectedFilterId(filter.id);
  };

  const loadSavedFilters = async () => {
    try {
      setFiltersLoading(true);
      setFiltersError("");
      const res = await api.get("/saved-filters/activities");
      const items = res.data || [];
      setSavedFilters(items);
      const def = items.find((f) => f.is_default);
      if (def && !selectedFilterId && !academyTaskPlanning)
        applySavedFilter(def);
    } catch (e) {
      console.error(e);
      setFiltersError(
        formatBackendError(
          e,
          t("crm_activities_saved_filters_load_error", {
            defaultValue: "Failed to load saved filters",
          }),
        ),
      );
    } finally {
      setFiltersLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkSchedule();
    fetchActivities();
    fetchLookups();
    loadSavedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!academyTaskPlanning || !academyTaskId) {
      academySearchSeededTaskRef.current = "";
      setAcademySearchGuideVisible(false);
      return;
    }

    const academyTask = activities.find(
      (activity) =>
        isTaskWorkItem(activity) &&
        String(activity.__task_id) === String(academyTaskId),
    );
    if (!academyTask) return;

    const taskKey = String(academyTask.__task_id);
    if (academySearchSeededTaskRef.current === taskKey) return;
    academySearchSeededTaskRef.current = taskKey;

    setSearchQuery(academyTask.subject || academyTask.title || taskKey);
    setStatusFilter("active");
    setCurrentPage(1);
    setViewMode("kanban");
    setActiveMobileColumnId(getActivityColumnId(academyTask));
    setAcademySearchGuideVisible(true);
  }, [academyTaskId, academyTaskPlanning, activities]);

  useEffect(() => {
    if (!academySearchGuideVisible) return undefined;

    const timer = window.setTimeout(
      () => setAcademySearchGuideVisible(false),
      8000,
    );
    return () => window.clearTimeout(timer);
  }, [academySearchGuideVisible]);

  const filteredActivities = useMemo(() => {
    const global = searchQuery.trim().toLowerCase();
    return activities.filter((a) => {
      if (
        academyTaskPlanning &&
        academyTaskId &&
        (!isTaskWorkItem(a) || String(a.__task_id) !== String(academyTaskId))
      ) {
        return false;
      }
      if (
        statusFilter === "active" &&
        viewMode !== "kanban" &&
        a.status !== "planned"
      )
        return false;
      if (
        statusFilter &&
        statusFilter !== "active" &&
        a.status !== statusFilter
      )
        return false;
      if (typeFilter && a.type !== typeFilter) return false;
      if (channelFilter && a.channel !== channelFilter) return false;
      if (global) {
        const fields = [
          a.ui_id,
          a.type,
          a.subject,
          a.content,
          a.channel,
          a.direction,
          a.status,
          a.outcome,
          a.contact_ui_id,
          a.lead_ui_id,
          a.deal_ui_id,
          a.owner_ui_id,
          a.when_at,
          a.created_at,
        ];
        return fields.some((field) => normalize(field).includes(global));
      }
      return true;
    });
  }, [
    activities,
    searchQuery,
    statusFilter,
    typeFilter,
    channelFilter,
    viewMode,
    academyTaskPlanning,
    academyTaskId,
  ]);

  const filteredTableRows = useMemo(() => {
    const rows = filteredActivities.filter((a) => {
      const title = `${normalize(a.subject)} ${normalize(a.content)}`;
      if (
        columnFilters.ui_id &&
        !normalize(a.ui_id).includes(columnFilters.ui_id.trim())
      )
        return false;
      if (
        columnFilters.subject &&
        !title.includes(columnFilters.subject.trim().toLowerCase())
      )
        return false;
      if (
        columnFilters.type &&
        !normalize(a.type).includes(columnFilters.type.trim().toLowerCase())
      )
        return false;
      if (
        columnFilters.status &&
        !normalize(a.status).includes(columnFilters.status.trim().toLowerCase())
      )
        return false;
      if (
        columnFilters.channel &&
        !normalize(a.channel).includes(
          columnFilters.channel.trim().toLowerCase(),
        )
      )
        return false;
      if (
        columnFilters.direction &&
        !normalize(a.direction).includes(
          columnFilters.direction.trim().toLowerCase(),
        )
      )
        return false;
      if (
        columnFilters.contact_ui_id &&
        !normalize(a.contact_ui_id).includes(columnFilters.contact_ui_id.trim())
      )
        return false;
      if (
        columnFilters.lead_ui_id &&
        !normalize(a.lead_ui_id).includes(columnFilters.lead_ui_id.trim())
      )
        return false;
      if (
        columnFilters.deal_ui_id &&
        !normalize(a.deal_ui_id).includes(columnFilters.deal_ui_id.trim())
      )
        return false;
      if (
        columnFilters.owner_ui_id &&
        !normalize(a.owner_ui_id).includes(columnFilters.owner_ui_id.trim())
      )
        return false;
      if (
        columnFilters.when_at &&
        !normalize(formatDateTime(a.when_at, t)).includes(
          columnFilters.when_at.trim().toLowerCase(),
        )
      )
        return false;
      if (
        columnFilters.created_at &&
        !normalize(formatDateTime(a.created_at, t)).includes(
          columnFilters.created_at.trim().toLowerCase(),
        )
      )
        return false;
      return true;
    });
    return sortRows(rows, sortConfig);
  }, [filteredActivities, columnFilters, sortConfig, t]);

  const totalRows = filteredTableRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * rowsPerPage;
  const visibleTableRows = filteredTableRows.slice(
    startIdx,
    startIdx + rowsPerPage,
  );
  const tableShowingText =
    totalRows === 0
      ? t("crm_activities_no_activities", { defaultValue: "No activities" })
      : t("crm_activities_table_showing", {
          defaultValue: "Showing {{from}} to {{to}} of {{total}} entries",
          from: startIdx + 1,
          to: Math.min(startIdx + rowsPerPage, totalRows),
          total: totalRows,
        });

  const groupedActivities = useMemo(() => {
    const base = {
      overdue: [],
      today: [],
      upcoming: [],
      noDate: [],
      done: [],
      canceled: [],
    };
    filteredActivities.forEach((a) => base[getActivityColumnId(a)].push(a));
    const sortByDate = (items) =>
      [...items].sort((a, b) => {
        const av = a.when_at ? new Date(a.when_at).getTime() : Infinity;
        const bv = b.when_at ? new Date(b.when_at).getTime() : Infinity;
        return av - bv;
      });
    return {
      overdue: sortByDate(base.overdue),
      today: sortByDate(base.today),
      upcoming: sortByDate(base.upcoming),
      noDate: [...base.noDate].sort((a, b) => (b.ui_id ?? 0) - (a.ui_id ?? 0)),
      done: sortByDate(base.done).reverse(),
      canceled: sortByDate(base.canceled).reverse(),
    };
  }, [filteredActivities]);

  const activityByKey = useMemo(() => {
    const map = {};
    activities.forEach((a) => {
      if (a.ui_id != null) map[activityKey(a.ui_id)] = a;
    });
    return map;
  }, [activities]);

  useEffect(() => {
    const next = {};
    ACTIVITY_COLUMNS.forEach((c) => {
      next[columnKey(c.id)] = (groupedActivities[c.id] || []).map((a) =>
        activityKey(a.ui_id),
      );
    });
    setContainers(next);
  }, [groupedActivities, ACTIVITY_COLUMNS]);

  const stats = useMemo(() => {
    let overdue = 0,
      today = 0,
      upcoming = 0,
      noDate = 0;
    activities.forEach((a) => {
      if (a.status !== "planned") return;
      const col = getActivityColumnId(a);
      if (col === "overdue") overdue += 1;
      if (col === "today") today += 1;
      if (col === "upcoming") upcoming += 1;
      if (col === "noDate") noDate += 1;
    });
    return { overdue, today, upcoming, noDate };
  }, [activities]);

  const targetRequiredInvalid =
    formSubmitted &&
    !form.contact_ui_id &&
    !form.lead_ui_id &&
    !form.deal_ui_id;

  const findContainerOfActivityKey = (key) => {
    const id = String(key);
    for (const [containerId, ids] of Object.entries(containers)) {
      if (ids.includes(id)) return containerId;
    }
    return null;
  };

  const logTaskTimelineEvent = async (activity, subject, content) => {
    if (!isTaskWorkItem(activity)) return;
    try {
      await api.post("/activities", {
        type: "note",
        subject,
        content,
        when_at: new Date().toISOString(),
        contact_ui_id: activity.contact_ui_id || null,
        lead_ui_id: activity.lead_ui_id || null,
        deal_ui_id: activity.deal_ui_id || null,
      });
    } catch (err) {
      // Timeline logging must never block the real task action.
      console.warn("[TIMELINE][TASK_EVENT_SKIPPED]", err);
    }
  };

  const describeTaskMove = (activity, targetColumnId, nextDueAt) => {
    const previousDue = activity.when_at
      ? formatDateTime(activity.when_at, t)
      : t("crm_activities_no_date", { defaultValue: "No date" });
    const nextDue = nextDueAt
      ? formatDateTime(nextDueAt, t)
      : t("crm_activities_no_date", { defaultValue: "No date" });
    if (targetColumnId === "today") {
      return {
        subject: t("crm_timeline_task_moved_today", {
          defaultValue: "Task moved to Today",
        }),
        content: `${getActivityTitle(activity, t)}\nDue date: ${previousDue} → ${nextDue}`,
      };
    }
    if (targetColumnId === "upcoming") {
      return {
        subject: t("crm_timeline_task_scheduled", {
          defaultValue: "Task scheduled",
        }),
        content: `${getActivityTitle(activity, t)}\nDue date: ${previousDue} → ${nextDue}`,
      };
    }
    if (targetColumnId === "noDate") {
      return {
        subject: t("crm_timeline_task_date_removed", {
          defaultValue: "Task date removed",
        }),
        content: `${getActivityTitle(activity, t)}\nDue date: ${previousDue} → ${nextDue}`,
      };
    }
    if (targetColumnId === "done") {
      return {
        subject: t("crm_timeline_task_completed", {
          defaultValue: "Task completed",
        }),
        content: `${getActivityTitle(activity, t)}\nPrevious status: ${getStatusLabel(activity.status, t)} → ${t("crm_activities_status_done", { defaultValue: "Done" })}`,
      };
    }
    return {
      subject: t("crm_timeline_task_updated", { defaultValue: "Task updated" }),
      content: getActivityTitle(activity, t),
    };
  };

  const buildActivityPutPayload = (activity, patch = {}) => ({
    type: patch.type ?? activity.type ?? "note",
    subject: Object.prototype.hasOwnProperty.call(patch, "subject")
      ? patch.subject
      : (activity.subject ?? null),
    content: Object.prototype.hasOwnProperty.call(patch, "content")
      ? patch.content
      : (activity.content ?? null),
    when_at: Object.prototype.hasOwnProperty.call(patch, "when_at")
      ? patch.when_at
      : (activity.when_at ?? null),
    channel: Object.prototype.hasOwnProperty.call(patch, "channel")
      ? patch.channel
      : (activity.channel ?? null),
    direction: Object.prototype.hasOwnProperty.call(patch, "direction")
      ? patch.direction
      : (activity.direction ?? null),
    status: patch.status ?? activity.status ?? "planned",
    contact_ui_id: Object.prototype.hasOwnProperty.call(patch, "contact_ui_id")
      ? patch.contact_ui_id
      : (activity.contact_ui_id ?? null),
    lead_ui_id: Object.prototype.hasOwnProperty.call(patch, "lead_ui_id")
      ? patch.lead_ui_id
      : (activity.lead_ui_id ?? null),
    deal_ui_id: Object.prototype.hasOwnProperty.call(patch, "deal_ui_id")
      ? patch.deal_ui_id
      : (activity.deal_ui_id ?? null),
  });

  const prepareMoveAction = (activity, targetColumnId) => {
    if (!activity?.ui_id) return;

    if (targetColumnId === "overdue") {
      setError(
        t("crm_activities_overdue_auto_only", {
          defaultValue:
            "Overdue is calculated automatically from due date. Move the task to Today, Upcoming, No date or Done instead.",
        }),
      );
      fetchActivities();
      return;
    }

    if (targetColumnId === "canceled" && isTaskWorkItem(activity)) {
      setError(
        t("crm_activities_task_cancel_not_supported", {
          defaultValue:
            "Tasks do not have a canceled status yet. Delete or complete the task instead.",
        }),
      );
      fetchActivities();
      return;
    }

    if (targetColumnId === "today") {
      const date = getTodayDateInputValue();
      const preferredTime = toTimeInputValue(activity.when_at);
      setPlanningMove({
        activity,
        targetColumnId,
        mode: "today",
        date,
        time: getFirstFreeSlot(activities, date, activity.ui_id, preferredTime),
      });
      return;
    }

    if (targetColumnId === "upcoming") {
      const date = getTomorrowDateInputValue();
      const preferredTime = toTimeInputValue(activity.when_at);
      setPlanningMove({
        activity,
        targetColumnId,
        mode: "upcoming",
        date,
        time: getFirstFreeSlot(activities, date, activity.ui_id, preferredTime),
      });
      return;
    }

    if (targetColumnId === "noDate") {
      setPlanningMove({ activity, targetColumnId, mode: "noDate" });
      return;
    }

    if (targetColumnId === "done") {
      setPlanningMove({ activity, targetColumnId, mode: "done" });
      return;
    }

    if (targetColumnId === "canceled") {
      setPlanningMove({ activity, targetColumnId, mode: "canceled" });
    }
  };

  const applyMoveAction = async () => {
    const move = planningMove;
    if (!move?.activity) return;
    const { activity, targetColumnId } = move;

    try {
      setSaving(true);
      setError("");

      if (isTaskWorkItem(activity)) {
        const payload = {};
        let nextDueAt = activity.when_at || null;
        if (targetColumnId === "done") {
          payload.status = "done";
        } else {
          payload.status = "pending";
          if (targetColumnId === "today" || targetColumnId === "upcoming") {
            const dueAt = combineDateAndTime(move.date, move.time);
            if (!dueAt)
              throw new Error(
                t("crm_activities_select_valid_date", {
                  defaultValue: "Select a valid date and time.",
                }),
              );
            payload.due_at = dueAt;
            nextDueAt = dueAt;
          }
          if (targetColumnId === "noDate") {
            payload.due_at = null;
            nextDueAt = null;
          }
        }
        await api.put(`/tasks/${activity.__task_id}`, payload);
        const event = describeTaskMove(activity, targetColumnId, nextDueAt);
        await logTaskTimelineEvent(activity, event.subject, event.content);
      } else {
        if (targetColumnId === "done") {
          await api.patch(`/activities/${activity.ui_id}/done`, {
            outcome: "completed",
          });
        } else if (targetColumnId === "canceled") {
          await api.patch(`/activities/${activity.ui_id}/cancel`);
        } else {
          const patch = { status: "planned" };
          if (targetColumnId === "today" || targetColumnId === "upcoming") {
            const whenAt = combineDateAndTime(move.date, move.time);
            if (!whenAt)
              throw new Error(
                t("crm_activities_select_valid_date", {
                  defaultValue: "Select a valid date and time.",
                }),
              );
            patch.when_at = whenAt;
          }
          if (targetColumnId === "noDate") patch.when_at = null;
          await api.put(
            `/activities/${activity.ui_id}`,
            buildActivityPutPayload(activity, patch),
          );
        }
      }

      setPlanningMove(null);
      await fetchActivities();
      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("crm_activities_move_error", {
            defaultValue: "Failed to move activity",
          }),
        ),
      );
      await fetchActivities();
    } finally {
      setSaving(false);
    }
  };

  const cancelMoveAction = async () => {
    setPlanningMove(null);
    await fetchActivities();
  };

  const handleDragStart = (event) => {
    const id = String(event.active.id);
    if (!id.startsWith(ACTIVITY_PREFIX)) return;
    activeFromContainerRef.current = findContainerOfActivityKey(id);
    setActiveActivityKey(id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (!activeId.startsWith(ACTIVITY_PREFIX)) return;
    const activeContainer = findContainerOfActivityKey(activeId);
    const overContainer = overId.startsWith(COLUMN_PREFIX)
      ? overId
      : findContainerOfActivityKey(overId);
    if (!activeContainer || !overContainer || activeContainer === overContainer)
      return;
    setContainers((prev) => {
      const next = { ...prev };
      next[activeContainer] = (next[activeContainer] || []).filter(
        (x) => x !== activeId,
      );
      next[overContainer] = [...(next[overContainer] || []), activeId];
      return next;
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const activeId = String(active?.id || "");
    const overId = String(over?.id || "");
    const startedFrom = activeFromContainerRef.current;
    activeFromContainerRef.current = null;
    setActiveActivityKey(null);
    if (!over || !activeId.startsWith(ACTIVITY_PREFIX)) return;
    const toContainer = overId.startsWith(COLUMN_PREFIX)
      ? overId
      : findContainerOfActivityKey(overId);
    if (!startedFrom || !toContainer) return;

    if (startedFrom === toContainer) {
      const ids = containers[startedFrom] || [];
      const oldIndex = ids.indexOf(activeId);
      const newIndex = ids.indexOf(overId);
      if (
        overId.startsWith(ACTIVITY_PREFIX) &&
        oldIndex !== -1 &&
        newIndex !== -1 &&
        oldIndex !== newIndex
      ) {
        setContainers((prev) => ({
          ...prev,
          [startedFrom]: arrayMove(prev[startedFrom], oldIndex, newIndex),
        }));
      }
      return;
    }

    const activity = activityByKey[activeId];
    if (!activity) return;
    const targetColumnId = parseColumnId(toContainer);
    prepareMoveAction(activity, targetColumnId);
  };

  const visibleKanbanColumns = useMemo(() => {
    if (!hideEmpty) return ACTIVITY_COLUMNS;
    return ACTIVITY_COLUMNS.filter(
      (col) => (containers[columnKey(col.id)] || []).length > 0,
    );
  }, [containers, hideEmpty, ACTIVITY_COLUMNS]);

  const activeActivity = activeActivityKey
    ? activityByKey[activeActivityKey]
    : null;

  const lookupLabelByUiId = (options, uiId, fallbackPrefix) => {
    const value = String(uiId || "").trim();
    if (!value) return "";
    const item = options.find((o) => String(o.ui_id) === value);
    if (!item) return `${fallbackPrefix} #${value}`;
    return item.title || `${fallbackPrefix} #${value}`;
  };

  const compactEntityText = (title, meta, onClick) => {
    if (!title) return "—";
    const inner = (
      <>
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#8fb3ff",
            fontWeight: 750,
          }}
        >
          {title}
        </span>
        {meta ? (
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "rgba(255,255,255,0.52)",
              fontSize: 11,
              marginTop: 2,
              fontWeight: 600,
            }}
          >
            {meta}
          </span>
        ) : null}
      </>
    );

    if (!onClick) {
      return (
        <span style={{ display: "block", lineHeight: 1.16, maxWidth: 220 }}>
          {inner}
        </span>
      );
    }

    return (
      <button
        type="button"
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          display: "block",
          lineHeight: 1.16,
          maxWidth: 220,
          minWidth: 0,
          textAlign: "left",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {inner}
      </button>
    );
  };

  const renderActivityContact = (a) => {
    if (!a?.contact_ui_id) return "—";
    return compactEntityText(
      a.contact_display_name ||
        lookupLabelByUiId(contactOptions, a.contact_ui_id, "Contact"),
      a.contact_primary_phone ||
        a.contact_primary_email ||
        `Contact #${a.contact_ui_id}`,
      () => navigate(`/menu/sales/contacts?contact_ui_id=${a.contact_ui_id}`),
    );
  };

  const renderActivityLead = (a) => {
    if (!a?.lead_ui_id) return "—";
    return compactEntityText(
      a.lead_title || lookupLabelByUiId(leadOptions, a.lead_ui_id, "Lead"),
      `Lead #${a.lead_ui_id}`,
      () => navigate(`/menu/sales/leads?lead_ui_id=${a.lead_ui_id}`),
    );
  };

  const renderActivityDeal = (a) => {
    if (!a?.deal_ui_id) return "—";
    return compactEntityText(
      a.deal_title || lookupLabelByUiId(dealOptions, a.deal_ui_id, "Deal"),
      `Deal #${a.deal_ui_id}`,
      () => navigate(`/menu/sales/deals?deal_ui_id=${a.deal_ui_id}`),
    );
  };

  const renderActivityOwner = (a) => {
    if (!a?.owner_ui_id && !a?.owner_id) return "—";
    return compactEntityText(
      a.owner_display_name ||
        a.owner_full_name ||
        a.owner_email ||
        `Employee #${a.owner_ui_id || a.owner_id}`,
      a.owner_email || (a.owner_ui_id ? `Employee #${a.owner_ui_id}` : ""),
      () =>
        navigate(
          `/menu/employees?user_id=${a.owner_id || ""}&user_ui_id=${a.owner_ui_id || ""}`,
        ),
    );
  };

  const openCreate = () => {
    setEditing(null);
    setDetailsActivity(null);
    setForm(emptyForm);
    setFormSubmitted(false);
    setShowForm(true);
    setError("");
  };

  useEffect(() => {
    if (!academyActivityCreate) {
      academyActivityFormOpenedRef.current = false;
      return;
    }

    if (academyActivityFormOpenedRef.current) return;
    academyActivityFormOpenedRef.current = true;

    setEditing(null);
    setDetailsActivity(null);
    setForm({
      ...emptyForm,
      type: "call",
      subject: academyActivityTitle,
      content: academyActivityDescription,
      status: "done",
      contact_ui_id: academyActivityContactUiId,
      deal_ui_id: academyActivityDealUiId,
    });
    setFormSubmitted(false);
    setShowForm(true);
    setError("");
  }, [
    academyActivityContactUiId,
    academyActivityCreate,
    academyActivityDealUiId,
    academyActivityDescription,
    academyActivityTitle,
  ]);

  const openEdit = (activity) => {
    setEditing(activity);
    setDetailsActivity(null);
    setFormSubmitted(false);
    setForm({
      type: activity.type || "note",
      subject: activity.subject || "",
      content: activity.content || "",
      when_at: toDateTimeLocal(activity.when_at),
      channel: activity.channel || "",
      direction: activity.direction || "",
      status: activity.status || "planned",
      contact_ui_id: activity.contact_ui_id
        ? String(activity.contact_ui_id)
        : "",
      lead_ui_id: activity.lead_ui_id ? String(activity.lead_ui_id) : "",
      deal_ui_id: activity.deal_ui_id ? String(activity.deal_ui_id) : "",
    });
    setShowForm(true);
    setError("");
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setFormSubmitted(false);
    setSaving(false);
  };

  const buildPayload = () => ({
    type: form.type,
    subject: form.subject.trim() || null,
    content: form.content.trim() || null,
    when_at: form.when_at || null,
    channel: form.channel || null,
    direction: form.direction || null,
    status: form.status || "planned",
    contact_ui_id: form.contact_ui_id ? Number(form.contact_ui_id) : null,
    lead_ui_id: form.lead_ui_id ? Number(form.lead_ui_id) : null,
    deal_ui_id: form.deal_ui_id ? Number(form.deal_ui_id) : null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    try {
      setSaving(true);
      setError("");
      const payload = buildPayload();
      if (
        !payload.contact_ui_id &&
        !payload.lead_ui_id &&
        !payload.deal_ui_id
      ) {
        setError(
          t("crm_activities_select_target_error", {
            defaultValue: "Select at least one target: contact, lead or deal.",
          }),
        );
        return;
      }
      if (editing?.ui_id)
        await api.put(`/activities/${editing.ui_id}`, payload);
      else await api.post("/activities", payload);

      if (academyActivityCreate) {
        window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
      }

      closeForm();
      await fetchActivities();
    } catch (e2) {
      console.error(e2);
      setError(
        formatBackendError(
          e2,
          t("crm_activities_save_error", {
            defaultValue: "Failed to save activity",
          }),
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activity) => {
    if (!activity?.ui_id) return;
    if (
      !window.confirm(
        t("crm_activities_delete_confirm", {
          defaultValue: "Delete this item?",
        }),
      )
    )
      return;
    try {
      setActionLoadingId(activity.ui_id);
      setError("");
      if (isTaskWorkItem(activity)) {
        await api.delete(`/tasks/${activity.__task_id}`);
      } else {
        await api.delete(`/activities/${activity.ui_id}`);
      }
      setDetailsActivity(null);
      await fetchActivities();
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("crm_activities_delete_error", {
            defaultValue: "Failed to delete item",
          }),
        ),
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDone = async (activity) => {
    if (!activity?.ui_id) return;
    try {
      setActionLoadingId(activity.ui_id);
      setError("");
      if (isTaskWorkItem(activity)) {
        await api.put(`/tasks/${activity.__task_id}`, { status: "done" });
        const event = describeTaskMove(
          activity,
          "done",
          activity.when_at || null,
        );
        await logTaskTimelineEvent(activity, event.subject, event.content);
      } else {
        await api.patch(`/activities/${activity.ui_id}/done`, {
          outcome: "completed",
        });
      }
      setDetailsActivity(null);
      await fetchActivities();
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("crm_activities_mark_done_error", {
            defaultValue: "Failed to mark item as done",
          }),
        ),
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleCancel = async (activity) => {
    if (!activity?.ui_id) return;
    if (isTaskWorkItem(activity)) {
      setError(
        t("crm_activities_task_cancel_not_supported", {
          defaultValue:
            "Tasks do not have a canceled status yet. Delete or complete the task instead.",
        }),
      );
      return;
    }
    if (
      !window.confirm(
        t("crm_activities_cancel_confirm", {
          defaultValue: "Cancel this activity?",
        }),
      )
    )
      return;
    try {
      setActionLoadingId(activity.ui_id);
      setError("");
      await api.patch(`/activities/${activity.ui_id}/cancel`);
      setDetailsActivity(null);
      await fetchActivities();
    } catch (e) {
      console.error(e);
      setError(
        formatBackendError(
          e,
          t("crm_activities_cancel_error", {
            defaultValue: "Failed to cancel activity",
          }),
        ),
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRefresh = async () => {
    setSearchQuery("");
    setStatusFilter("active");
    setTypeFilter("");
    setChannelFilter("");
    setColumnFilters(defaultColumnFilters);
    setCurrentPage(1);
    setRowsPerPage(10);
    setSortConfig({ key: "when_at", direction: "asc" });
    setSelectedFilterId("");
    setFiltersError("");
    await fetchActivities();
    await fetchLookups();
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortArrow = (key) =>
    sortConfig.key !== key ? "" : sortConfig.direction === "asc" ? " ↑" : " ↓";

  const buildSavedFilterPayload = (name, isDefault) => ({
    name,
    filters: {
      searchQuery,
      statusFilter,
      typeFilter,
      channelFilter,
      columnFilters,
      sortConfig,
      rowsPerPage,
      viewMode,
    },
    sort_by: sortConfig.key,
    sort_dir: sortConfig.direction,
    per_page: rowsPerPage,
    is_default: isDefault,
  });

  const handleSelectSavedFilter = (e) => {
    const value = e.target.value;
    if (!value) {
      setSelectedFilterId("");
      return;
    }
    const filter = savedFilters.find((x) => x.id === Number(value));
    if (filter) applySavedFilter(filter);
  };

  const handleSaveCurrentAsNew = async () => {
    const name = window.prompt(
      t("crm_activities_filter_name_prompt", { defaultValue: "Filter name" }),
    );
    if (!name?.trim()) return;
    const makeDefault = window.confirm(
      t("crm_activities_filter_make_default_confirm", {
        defaultValue: "Make this filter default?",
      }),
    );
    try {
      setSavingFilter(true);
      setFiltersError("");
      await api.post(
        "/saved-filters/activities",
        buildSavedFilterPayload(name.trim(), makeDefault),
      );
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        formatBackendError(
          e,
          t("crm_activities_filter_save_error", {
            defaultValue: "Failed to save filter",
          }),
        ),
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleUpdateSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(
        t("crm_activities_select_filter_first", {
          defaultValue: "Select a filter first",
        }),
      );
      return;
    }
    const makeDefault = window.confirm(
      t("crm_activities_filter_make_default_confirm", {
        defaultValue: "Make this filter default?",
      }),
    );
    try {
      setSavingFilter(true);
      setFiltersError("");
      await api.put(
        `/saved-filters/${selectedFilterId}`,
        buildSavedFilterPayload(undefined, makeDefault),
      );
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        formatBackendError(
          e,
          t("crm_activities_filter_update_error", {
            defaultValue: "Failed to update filter",
          }),
        ),
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteSelectedFilter = async () => {
    if (!selectedFilterId) {
      alert(
        t("crm_activities_select_filter_first", {
          defaultValue: "Select a filter first",
        }),
      );
      return;
    }
    if (
      !window.confirm(
        t("crm_activities_filter_delete_confirm", {
          defaultValue: "Delete selected filter?",
        }),
      )
    )
      return;
    try {
      setSavingFilter(true);
      setFiltersError("");
      await api.delete(`/saved-filters/${selectedFilterId}`);
      setSelectedFilterId("");
      await loadSavedFilters();
    } catch (e) {
      console.error(e);
      setFiltersError(
        formatBackendError(
          e,
          t("crm_activities_filter_delete_error", {
            defaultValue: "Failed to delete filter",
          }),
        ),
      );
    } finally {
      setSavingFilter(false);
    }
  };

  const renderKanban = () => (
    <div
      data-academy={academyTaskPlanning ? "activities-board" : undefined}
      className="activities-kanban-wrap"
    >
      <div className="activities-kanban-topbar">
        <div className="activities-kanban-hint">
          {saving
            ? t("saving", { defaultValue: "Saving..." })
            : t("crm_activities_kanban_hint", {
                defaultValue:
                  "Click a card to view details. Drag cards between columns to reschedule or change status.",
              })}
        </div>
        <div className="activities-kanban-controls">
          <div
            className="btn-group btn-group-sm"
            role="group"
            aria-label={t("crm_activities_density", {
              defaultValue: "Density",
            })}
          >
            <button
              type="button"
              className={`btn ${density === "comfortable" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setDensity("comfortable")}
              disabled={loading || saving}
            >
              {t("crm_activities_density_comfortable", {
                defaultValue: "Comfortable",
              })}
            </button>
            <button
              type="button"
              className={`btn ${density === "compact" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setDensity("compact")}
              disabled={loading || saving}
            >
              {t("crm_activities_density_compact", { defaultValue: "Compact" })}
            </button>
          </div>
          <button
            type="button"
            className={`btn btn-sm ${hideEmpty ? "btn-secondary" : "btn-outline-secondary"}`}
            onClick={() => setHideEmpty((v) => !v)}
            disabled={loading || saving}
          >
            {t("crm_activities_hide_empty", { defaultValue: "Hide empty" })}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="activities-loading">
          {t("crm_activities_loading_activities", {
            defaultValue: "Loading activities...",
          })}
        </div>
      ) : (
        <>
          <div
            className="activities-mobile-column-tabs"
            aria-label={t("crm_activities_columns", {
              defaultValue: "Activity columns",
            })}
          >
            {ACTIVITY_COLUMNS.map((col) => {
              const count = (containers[columnKey(col.id)] || []).length;

              return (
                <button
                  key={col.id}
                  type="button"
                  className={`activities-mobile-column-tab ${col.tone || "default"} ${
                    activeMobileColumnId === col.id ? "active" : ""
                  }`}
                  title={col.title}
                  aria-pressed={
                    activeMobileColumnId === col.id ? "true" : "false"
                  }
                  onClick={() => {
                    setActiveMobileColumnId(col.id);

                    document
                      .getElementById(`activity-mobile-column-${col.id}`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest",
                      });
                  }}
                >
                  <span className="activities-mobile-column-dot" />
                  <span className="activities-mobile-column-label">
                    {col.title}
                  </span>
                  <strong>{count}</strong>
                </button>
              );
            })}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="activities-kanban-grid">
              {visibleKanbanColumns.map((col) => {
                const key = columnKey(col.id);
                return (
                  <div
                    key={key}
                    id={`activity-mobile-column-${col.id}`}
                    className="activities-mobile-column-anchor"
                  >
                    <ActivityKanbanColumn
                      id={key}
                      title={col.title}
                      subtitle={col.subtitle}
                      tone={col.tone}
                      activityKeys={containers[key] || []}
                      activityByKey={activityByKey}
                      disabled={loading || saving}
                      density={density}
                      contactOptions={contactOptions}
                      leadOptions={leadOptions}
                      dealOptions={dealOptions}
                      onOpen={setDetailsActivity}
                      onDone={handleDone}
                      onCancel={handleCancel}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      actionLoadingId={actionLoadingId}
                      academyTaskId={academyTaskId}
                      academySearchGuideVisible={academySearchGuideVisible}
                    />
                  </div>
                );
              })}
            </div>
            <DragOverlay>
              {activeActivity ? (
                <ActivityCardOverlay activity={activeActivity} />
              ) : null}
            </DragOverlay>
          </DndContext>
        </>
      )}
    </div>
  );

  const renderTableFilters = () => (
    <div className="activities-table-filters-panel">
      <div className="activities-table-filters-title">
        {t("crm_activities_table_filters", { defaultValue: "Table filters" })}
      </div>
      <div className="activities-table-filter-actions">
        <select
          className="form-select form-select-sm activities-saved-filters"
          value={selectedFilterId || ""}
          onChange={handleSelectSavedFilter}
          disabled={filtersLoading}
        >
          <option value="">
            {filtersLoading
              ? t("loading", { defaultValue: "Loading" }) + "..."
              : t("crm_activities_saved_filters", {
                  defaultValue: "Saved filters",
                })}
          </option>
          {savedFilters.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
              {f.is_default ? " ⭐" : ""}
            </option>
          ))}
        </select>
        <button
          className="btn btn-sm btn-outline-success"
          type="button"
          title={t("crm_activities_save_filter", {
            defaultValue: "Save filter",
          })}
          onClick={handleSaveCurrentAsNew}
          disabled={savingFilter}
        >
          <i className="lni lni-floppy-disk-1"></i>
        </button>
        <button
          className="btn btn-sm btn-outline-primary"
          type="button"
          title={t("crm_activities_update_filter", {
            defaultValue: "Update filter",
          })}
          onClick={handleUpdateSelectedFilter}
          disabled={savingFilter || !selectedFilterId}
        >
          <i className="lni lni-refresh-circle-1-clockwise"></i>
        </button>
        <button
          className="btn btn-sm btn-outline-danger"
          type="button"
          title={t("crm_activities_delete_filter", {
            defaultValue: "Delete filter",
          })}
          onClick={handleDeleteSelectedFilter}
          disabled={savingFilter || !selectedFilterId}
        >
          <i className="lni lni-trash-3"></i>
        </button>
      </div>
    </div>
  );

  const renderTablePagination = () => (
    <div className="activities-table-pagination">
      <div className="activities-table-left">
        <select
          className="form-select form-select-sm activities-page-size"
          value={rowsPerPage}
          onChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span className="text-muted">{tableShowingText}</span>
      </div>
      <ul className="pagination pagination-sm mb-0">
        <li className={`page-item ${safePage <= 1 ? "disabled" : ""}`}>
          <button
            type="button"
            className="page-link"
            title={t("previous", { defaultValue: "Previous" })}
            aria-label={t("previous", { defaultValue: "Previous" })}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            <i className="lni lni-arrow-left" />
          </button>
        </li>
        {Array.from({ length: totalPages }, (_, idx) => (
          <li
            key={idx}
            className={`page-item ${safePage === idx + 1 ? "active" : ""}`}
          >
            <button
              type="button"
              className="page-link"
              onClick={() => setCurrentPage(idx + 1)}
            >
              {idx + 1}
            </button>
          </li>
        ))}
        <li className={`page-item ${safePage >= totalPages ? "disabled" : ""}`}>
          <button
            type="button"
            className="page-link"
            title={t("next", { defaultValue: "Next" })}
            aria-label={t("next", { defaultValue: "Next" })}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            <i className="lni lni-arrow-right" />
          </button>
        </li>
      </ul>
    </div>
  );

  const renderTable = () => (
    <div className="activities-table-card">
      {renderTableFilters()}
      <div className="activities-table-scroll">
        <table className="table table-striped table-hover align-middle activities-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("ui_id")}>
                {t("id", { defaultValue: "ID" })}
                {renderSortArrow("ui_id")}
              </th>
              <th onClick={() => handleSort("subject")}>
                {t("crm_activities_action", { defaultValue: "Action" })}
                {renderSortArrow("subject")}
              </th>
              <th onClick={() => handleSort("type")}>
                {t("crm_activities_type", { defaultValue: "Type" })}
                {renderSortArrow("type")}
              </th>
              <th onClick={() => handleSort("status")}>
                {t("crm_activities_status", { defaultValue: "Status" })}
                {renderSortArrow("status")}
              </th>
              <th onClick={() => handleSort("channel")}>
                {t("crm_activities_channel", { defaultValue: "Channel" })}
                {renderSortArrow("channel")}
              </th>
              <th onClick={() => handleSort("direction")}>
                {t("crm_activities_direction", { defaultValue: "Direction" })}
                {renderSortArrow("direction")}
              </th>
              <th onClick={() => handleSort("contact_ui_id")}>
                {t("crm_activities_contact", { defaultValue: "Contact" })}
                {renderSortArrow("contact_ui_id")}
              </th>
              <th onClick={() => handleSort("lead_ui_id")}>
                {t("crm_activities_lead", { defaultValue: "Lead" })}
                {renderSortArrow("lead_ui_id")}
              </th>
              <th onClick={() => handleSort("deal_ui_id")}>
                {t("crm_activities_deal", { defaultValue: "Deal" })}
                {renderSortArrow("deal_ui_id")}
              </th>
              <th onClick={() => handleSort("owner_ui_id")}>
                {t("crm_activities_owner", { defaultValue: "Owner" })}
                {renderSortArrow("owner_ui_id")}
              </th>
              <th onClick={() => handleSort("when_at")}>
                {t("crm_activities_when", { defaultValue: "When" })}
                {renderSortArrow("when_at")}
              </th>
              <th onClick={() => handleSort("created_at")}>
                {t("crm_activities_created", { defaultValue: "Created" })}
                {renderSortArrow("created_at")}
              </th>
              <th>{t("actions", { defaultValue: "Actions" })}</th>
            </tr>
            <tr className="activities-column-filter-row">
              {Object.keys(defaultColumnFilters).map((key) => (
                <th key={key}>
                  <input
                    className="form-control form-control-sm"
                    placeholder={
                      key === "ui_id"
                        ? t("id", { defaultValue: "ID" })
                        : key === "subject"
                          ? t("crm_activities_action", {
                              defaultValue: "Action",
                            })
                          : key === "type"
                            ? t("crm_activities_type", {
                                defaultValue: "Type",
                              })
                            : key === "status"
                              ? t("crm_activities_status", {
                                  defaultValue: "Status",
                                })
                              : key === "channel"
                                ? t("crm_activities_channel", {
                                    defaultValue: "Channel",
                                  })
                                : key === "direction"
                                  ? t("crm_activities_direction", {
                                      defaultValue: "Direction",
                                    })
                                  : key === "contact_ui_id"
                                    ? t("crm_activities_contact", {
                                        defaultValue: "Contact",
                                      })
                                    : key === "lead_ui_id"
                                      ? t("crm_activities_lead", {
                                          defaultValue: "Lead",
                                        })
                                      : key === "deal_ui_id"
                                        ? t("crm_activities_deal", {
                                            defaultValue: "Deal",
                                          })
                                        : key === "owner_ui_id"
                                          ? t("crm_activities_owner", {
                                              defaultValue: "Owner",
                                            })
                                          : key === "when_at"
                                            ? t("crm_activities_when", {
                                                defaultValue: "When",
                                              })
                                            : key === "created_at"
                                              ? t("crm_activities_created", {
                                                  defaultValue: "Created",
                                                })
                                              : key
                    }
                    value={columnFilters[key]}
                    onChange={(e) =>
                      handleColumnFilterChange(key, e.target.value)
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
              ))}
              <th>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={() => {
                    setColumnFilters(defaultColumnFilters);
                    setCurrentPage(1);
                  }}
                >
                  {t("clear", { defaultValue: "Clear" })}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={13} className="text-center py-4">
                  {t("loading", { defaultValue: "Loading" })}...
                </td>
              </tr>
            )}
            {!loading && visibleTableRows.length === 0 && (
              <tr>
                <td colSpan={13} className="activities-empty">
                  <div className="activities-empty-title">
                    {t("crm_activities_no_activities_found", {
                      defaultValue: "No activities found",
                    })}
                  </div>
                  <div className="activities-empty-text">
                    {t("crm_activities_empty_text", {
                      defaultValue:
                        "Create a note, call, meeting, message or email activity.",
                    })}
                  </div>
                  <button
                    data-academy="activities-create"
                    className="btn btn-primary"
                    onClick={openCreate}
                  >
                    {t("crm_activities_new_task_or_activity", {
                      defaultValue: "New activity",
                    })}
                  </button>
                </td>
              </tr>
            )}
            {!loading &&
              visibleTableRows.map((a) => {
                const isBusy = actionLoadingId === a.ui_id;
                return (
                  <tr key={a.id || a.ui_id}>
                    <td>{a.ui_id}</td>
                    <td className="activities-subject-cell">
                      <button
                        type="button"
                        className="activities-link-button"
                        onClick={() => setDetailsActivity(a)}
                      >
                        <div className="activities-subject">
                          {getActivityTitle(a, t)}
                        </div>
                        {a.content && (
                          <div className="activities-content-preview">
                            {String(a.content).slice(0, 90)}
                          </div>
                        )}
                      </button>
                    </td>
                    <td>
                      <span
                        className={`activities-badge type-${a.type || "none"}`}
                      >
                        {a.type ? getTypeLabel(a.type, t) : "—"}
                      </span>
                    </td>
                    <td>
                      <span className={`activities-status-pill ${a.status}`}>
                        {getStatusLabel(a.status, t)}
                      </span>
                    </td>
                    <td>{a.channel ? getChannelLabel(a.channel, t) : "—"}</td>
                    <td>
                      {a.direction ? getDirectionLabel(a.direction, t) : "—"}
                    </td>
                    <td>{renderActivityContact(a)}</td>
                    <td>{renderActivityLead(a)}</td>
                    <td>{renderActivityDeal(a)}</td>
                    <td>{renderActivityOwner(a)}</td>
                    <td>{formatDateTime(a.when_at, t)}</td>
                    <td>{formatDateTime(a.created_at, t)}</td>
                    <td>
                      <div className="activities-actions">
                        {a.status === "planned" && (
                          <button
                            className="btn btn-sm btn-success"
                            disabled={isBusy}
                            onClick={() => handleDone(a)}
                          >
                            {t("crm_activities_done", { defaultValue: "Done" })}
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={isBusy}
                          onClick={() => openEdit(a)}
                        >
                          {t("edit", { defaultValue: "Edit" })}
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          disabled={isBusy}
                          onClick={() => handleDelete(a)}
                        >
                          {t("delete", { defaultValue: "Delete" })}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {renderTablePagination()}
    </div>
  );

  return (
    <div className="activities-page container mt-4">
      <div data-academy="activities-header" className="activities-header">
        <div>
          <h2 className="mb-1">
            {t("sales_my_work", { defaultValue: "My Work" })}
          </h2>
          <p className="activities-subtitle">
            {t("crm_activities_page_subtitle", {
              defaultValue:
                "Your task-based action board. Imported, Telegram and manual tasks appear here automatically.",
            })}
          </p>
        </div>
        <div className="activities-header-actions">
          <button
            className="btn btn-outline-secondary"
            onClick={handleRefresh}
            disabled={loading || saving}
          >
            {loading
              ? t("loading", { defaultValue: "Loading" }) + "..."
              : t("refresh", { defaultValue: "Refresh" })}
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            {t("crm_activities_new_task_or_activity", {
              defaultValue: "New activity",
            })}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {filtersError && (
        <div className="alert alert-warning py-2">{filtersError}</div>
      )}

      <div
        data-academy="activities-summary"
        className="activities-work-summary"
      >
        <div>
          <div className="activities-kicker">
            {t("crm_activities_sales_work_kicker", {
              defaultValue: "Today’s sales work",
            })}
          </div>
          <h3>
            {t("crm_activities_work_summary_title", {
              defaultValue: "What should I do now?",
            })}
          </h3>
          <p>
            {t("crm_activities_work_summary_text", {
              defaultValue:
                "Focus on overdue and today’s tasks first. Imported, Telegram and manual tasks appear here automatically.",
            })}
          </p>
        </div>
        <div className="activities-stats">
          <button
            className="activities-stat danger"
            onClick={() => {
              setStatusFilter("active");
              setViewMode("kanban");
            }}
          >
            <strong>{stats.overdue}</strong>
            <span>
              {t("crm_activities_column_overdue", { defaultValue: "Overdue" })}
            </span>
          </button>
          <button
            className="activities-stat primary"
            onClick={() => {
              setStatusFilter("active");
              setViewMode("kanban");
            }}
          >
            <strong>{stats.today}</strong>
            <span>
              {t("crm_activities_column_today", { defaultValue: "Today" })}
            </span>
          </button>
          <button
            className="activities-stat"
            onClick={() => {
              setStatusFilter("active");
              setViewMode("kanban");
            }}
          >
            <strong>{stats.upcoming}</strong>
            <span>
              {t("crm_activities_column_upcoming", {
                defaultValue: "Upcoming",
              })}
            </span>
          </button>
          <button
            className="activities-stat"
            onClick={() => {
              setStatusFilter("active");
              setViewMode("kanban");
            }}
          >
            <strong>{stats.noDate}</strong>
            <span>
              {t("crm_activities_column_no_date", { defaultValue: "No date" })}
            </span>
          </button>
        </div>
      </div>

      <div data-academy="activities-filters" className="activities-toolbar">
        <div className="activities-toolbar-grid">
          <input
            data-academy={academyTaskPlanning ? "activities-search" : undefined}
            className={`form-control ${academySearchGuideVisible ? "academyWorkflowTarget" : ""}`}
            placeholder={t("crm_activities_search_placeholder", {
              defaultValue: "Search contact, lead, deal, subject...",
            })}
            value={searchQuery}
            onChange={(e) => {
              setAcademySearchGuideVisible(false);
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            autoFocus={academySearchGuideVisible}
          />
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="active">
              {t("crm_activities_status_active", { defaultValue: "Active" })}
            </option>
            {STATUSES.map((x) => (
              <option key={x} value={x}>
                {getStatusLabel(x, t)}
              </option>
            ))}
            <option value="">
              {t("crm_activities_all_statuses", {
                defaultValue: "All statuses",
              })}
            </option>
          </select>
          <select
            className="form-select"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">
              {t("crm_activities_all_types", { defaultValue: "All types" })}
            </option>
            {ACTIVITY_TYPES.map((x) => (
              <option key={x} value={x}>
                {getTypeLabel(x, t)}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            value={channelFilter}
            onChange={(e) => {
              setChannelFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">
              {t("crm_activities_all_channels", {
                defaultValue: "All channels",
              })}
            </option>
            {CHANNELS.map((x) => (
              <option key={x} value={x}>
                {getChannelLabel(x, t)}
              </option>
            ))}
          </select>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              setSearchQuery("");
              setStatusFilter("active");
              setTypeFilter("");
              setChannelFilter("");
              setColumnFilters(defaultColumnFilters);
              setCurrentPage(1);
            }}
          >
            {t("clear", { defaultValue: "Clear" })}
          </button>
        </div>
        <div
          data-academy="activities-view-switch"
          className="activities-toolbar-bottom"
        >
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className={`btn ${viewMode === "kanban" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("kanban")}
            >
              {t("crm_activities_board", { defaultValue: "Board" })}
            </button>
            <button
              type="button"
              className={`btn ${viewMode === "table" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setViewMode("table")}
            >
              {t("crm_activities_list", { defaultValue: "List" })}
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <ActivityFormModal
          form={form}
          setForm={setForm}
          editing={editing}
          saving={saving}
          lookupsError={lookupsError}
          lookupsLoading={lookupsLoading}
          contactOptions={contactOptions}
          leadOptions={leadOptions}
          dealOptions={dealOptions}
          targetRequiredInvalid={targetRequiredInvalid}
          academyMode={academyActivityCreate}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}

      {detailsActivity && (
        <ActivityDetailsModal
          activity={detailsActivity}
          isBusy={actionLoadingId === detailsActivity.ui_id}
          onClose={() => setDetailsActivity(null)}
          onEdit={() => openEdit(detailsActivity)}
          onDone={() => handleDone(detailsActivity)}
          onCancel={() => handleCancel(detailsActivity)}
          onDelete={() => handleDelete(detailsActivity)}
        />
      )}

      {planningMove && (
        <TaskPlanningModal
          move={planningMove}
          setMove={setPlanningMove}
          saving={saving}
          onClose={cancelMoveAction}
          onConfirm={applyMoveAction}
          allActivities={activities}
          workSchedule={workSchedule}
          academyTaskId={academyTaskId}
        />
      )}

      <div data-academy="activities-workspace">
        {viewMode === "kanban" && renderKanban()}
        {viewMode === "table" && renderTable()}
      </div>
    </div>
  );
};

export default ActivitiesPage;

function TaskPlanningModal({
  move,
  setMove,
  saving,
  onClose,
  onConfirm,
  allActivities = [],
  workSchedule = DEFAULT_WORK_SCHEDULE,
  academyTaskId = null,
}) {
  const { t } = useTranslation();
  const activity = move?.activity;
  const isAcademyTask = Boolean(
    academyTaskId &&
      activity?.__task_id &&
      String(activity.__task_id) === String(academyTaskId),
  );
  const title = activity
    ? `#${activity.ui_id} ${getActivityTitle(activity, t)}`
    : "";

  const isDateMode = move?.mode === "today" || move?.mode === "upcoming";
  const busySlots = useMemo(
    () => getBusyTaskSlots(allActivities, move?.date, activity?.ui_id),
    [allActivities, move?.date, activity?.ui_id],
  );
  const [serverAvailableTimeSlots, setServerAvailableTimeSlots] = useState(null);
  useEffect(() => {
    let active = true;
    if (!isDateMode || !move?.date) { setServerAvailableTimeSlots(null); return () => { active = false; }; }
    api.get("/auth/crmSettings/work-schedule/availability", { params: { schedule_date: move.date } })
      .then((res) => { if (active) setServerAvailableTimeSlots(Array.isArray(res?.data?.slots) ? res.data.slots : []); })
      .catch(() => { if (active) setServerAvailableTimeSlots(null); });
    return () => { active = false; };
  }, [isDateMode, move?.date]);
  const availableTimeSlots = useMemo(
    () => serverAvailableTimeSlots ?? getAvailableTimeSlotsForDate(move?.date, workSchedule),
    [serverAvailableTimeSlots, move?.date, workSchedule],
  );
  const selectedSlotIsBusy =
    isDateMode && move?.time && busySlots.has(move.time);
  const selectedSlotIsPast =
    isDateMode &&
    move?.time &&
    isPastPlanningSlot(move?.date, move.time, workSchedule);
  const selectedSlotIsOutsideSchedule =
    isDateMode && move?.time && !availableTimeSlots.includes(move.time);
  const selectedSlotIsInvalid = Boolean(
    selectedSlotIsBusy || selectedSlotIsPast || selectedSlotIsOutsideSchedule,
  );
  const freeSlotCount = availableTimeSlots.filter(
    (slot) => !busySlots.has(slot),
  ).length;
  const minimumSlot = isDateMode
    ? getMinimumPlanningSlotForDate(move?.date, workSchedule)
    : null;

  const chooseDate = (date) => {
    const preferred = move?.time || toTimeInputValue(activity?.when_at);
    setMove((prev) => ({
      ...prev,
      date,
      time: getFirstFreeSlot(
        allActivities,
        date,
        activity?.ui_id,
        preferred,
        workSchedule,
      ),
    }));
  };

  const pickNearestFree = () => {
    const date =
      move?.date ||
      (move?.mode === "today"
        ? getTodayDateInputValue()
        : getTomorrowDateInputValue());
    setMove((prev) => ({
      ...prev,
      date,
      time: getFirstFreeSlot(
        allActivities,
        date,
        activity?.ui_id,
        prev?.time,
        workSchedule,
      ),
    }));
  };

  const header =
    move?.mode === "today"
      ? t("crm_activities_move_to_today", {
          defaultValue: "Move task to today",
        })
      : move?.mode === "upcoming"
        ? t("crm_activities_schedule_upcoming", {
            defaultValue: "Schedule task",
          })
        : move?.mode === "noDate"
          ? t("crm_activities_clear_due_date", {
              defaultValue: "Remove due date",
            })
          : move?.mode === "done"
            ? t("crm_activities_complete_task", {
                defaultValue: "Complete task",
              })
            : t("crm_activities_change_task", { defaultValue: "Change task" });

  const message =
    move?.mode === "noDate"
      ? t("crm_activities_clear_due_date_confirm", {
          defaultValue:
            "This task will move to No date because due date will be removed.",
        })
      : move?.mode === "done"
        ? t("crm_activities_complete_task_confirm", {
            defaultValue: "This task will be marked as done.",
          })
        : t("crm_activities_select_due_date_help", {
            defaultValue:
              "The board column is calculated from the real due date. Choose the date and time to save.",
          });

  return (
    <div
      data-academy={isAcademyTask ? "activities-planning-modal" : undefined}
      className="activities-modal-backdrop"
    >
      <div className="activities-modal" role="dialog" aria-modal="true">
        <div className="activities-form-header">
          <div>
            <h5 className="mb-1">{header}</h5>
            <p className="activities-subtitle mb-0">{title}</p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t("close", { defaultValue: "Close" })}
          </button>
        </div>

        <p className="activities-subtitle">{message}</p>

        {isDateMode && (
          <p className="activities-subtitle">
            {workSchedule?.enabled
              ? t("crm_activities_work_schedule_enabled", {
                  defaultValue: "Working hours: {{start}} - {{end}}",
                  start: workSchedule.start_time,
                  end: workSchedule.end_time,
                })
              : t("crm_activities_work_schedule_full_day", {
                  defaultValue:
                    "No working hours limit: full day schedule is available.",
                })}
          </p>
        )}

        {isDateMode && (
          <div className="activities-smart-scheduler">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">
                  {t("date", { defaultValue: "Date" })}
                </label>
                <input
                  data-academy={
                    isAcademyTask ? "activities-planning-date" : undefined
                  }
                  type="date"
                  className="form-control"
                  value={move.date || ""}
                  min={
                    move.mode === "upcoming"
                      ? getTomorrowDateInputValue()
                      : getTodayDateInputValue()
                  }
                  onChange={(e) => chooseDate(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">
                  {t("time", { defaultValue: "Time" })}
                </label>
                <div className="activities-time-input-row">
                  <input
                    data-academy={
                      isAcademyTask ? "activities-planning-time" : undefined
                    }
                    type="time"
                    className={`form-control ${selectedSlotIsInvalid ? "is-invalid" : ""}`}
                    value={move.time || minimumSlot || "09:00"}
                    min={minimumSlot || undefined}
                    step={TASK_SLOT_MINUTES * 60}
                    onChange={(e) =>
                      setMove((prev) => ({ ...prev, time: e.target.value }))
                    }
                  />
                  <button
                    data-academy={
                      isAcademyTask ? "activities-find-free" : undefined
                    }
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={pickNearestFree}
                    disabled={saving || freeSlotCount <= 0}
                  >
                    {t("crm_activities_find_free_time", {
                      defaultValue: "Find free",
                    })}
                  </button>
                </div>
                {selectedSlotIsBusy ? (
                  <div className="invalid-feedback d-block">
                    {t("crm_activities_time_slot_busy", {
                      defaultValue:
                        "This time is already booked. Choose a green free slot.",
                    })}
                  </div>
                ) : selectedSlotIsPast ? (
                  <div className="invalid-feedback d-block">
                    {t("crm_activities_time_slot_past", {
                      defaultValue:
                        "You can schedule only from the current time plus 15 minutes.",
                    })}
                  </div>
                ) : selectedSlotIsOutsideSchedule ? (
                  <div className="invalid-feedback d-block">
                    {workSchedule?.enabled
                      ? t("crm_activities_time_outside_work_schedule", {
                          defaultValue:
                            "This time is outside the configured working hours.",
                        })
                      : t("crm_activities_time_not_available", {
                          defaultValue:
                            "This time is not available for the selected date.",
                        })}
                  </div>
                ) : minimumSlot && move?.date === getTodayDateInputValue() ? (
                  <div className="form-text">
                    {t("crm_activities_time_slot_minimum", {
                      defaultValue: "Available from {{time}} today.",
                      time: minimumSlot,
                    })}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="activities-slot-panel">
              <div className="activities-slot-panel-header">
                <strong>
                  {t("crm_activities_free_time", {
                    defaultValue: "Available time",
                  })}
                </strong>
                <span>
                  <span className="activities-slot-dot free" />{" "}
                  {t("crm_activities_slot_free", { defaultValue: "free" })}
                  <span className="activities-slot-dot busy" />{" "}
                  {t("crm_activities_slot_busy", { defaultValue: "busy" })}
                </span>
              </div>
              <div className="activities-time-slots">
                {availableTimeSlots.map((slot) => {
                  const busy = busySlots.has(slot);
                  const active = move.time === slot;
                  return (
                    <button
                      key={slot}
                      data-academy={
                        isAcademyTask ? "activities-free-slot" : undefined
                      }
                      type="button"
                      className={`activities-time-slot ${busy ? "busy" : "free"} ${active ? "active" : ""}`}
                      disabled={busy || saving}
                      onClick={() =>
                        setMove((prev) => ({ ...prev, time: slot }))
                      }
                      title={
                        busy
                          ? t("crm_activities_slot_busy_title", {
                              defaultValue: "Already booked",
                            })
                          : t("crm_activities_slot_free_title", {
                              defaultValue: "Free time",
                            })
                      }
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
              {freeSlotCount <= 0 ? (
                <div className="activities-slot-warning">
                  {t("crm_activities_no_free_slots", {
                    defaultValue:
                      "No free slots found for this day. Choose another date.",
                  })}
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div className="activities-form-actions mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            data-academy={
              isAcademyTask ? "activities-planning-save" : undefined
            }
            type="button"
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={
              saving ||
              selectedSlotIsInvalid ||
              (isDateMode && freeSlotCount <= 0)
            }
          >
            {saving
              ? t("saving", { defaultValue: "Saving..." })
              : t("save", { defaultValue: "Save" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActivityFormModal({
  form,
  setForm,
  editing,
  saving,
  lookupsError,
  lookupsLoading,
  contactOptions,
  leadOptions,
  dealOptions,
  targetRequiredInvalid,
  academyMode = false,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation();

  return (
    <div className="activities-modal-backdrop">
      <form
        className="activities-modal"
        data-academy={academyMode ? "activity-create-modal" : undefined}
        onSubmit={onSubmit}
        noValidate
      >
        <div className="activities-form-header">
          <div>
            <h5 className="mb-1">
              {editing
                ? t("crm_activities_edit_activity", {
                    defaultValue: "Edit activity",
                  })
                : t("crm_activities_create_activity", {
                    defaultValue: "Create activity",
                  })}
            </h5>
            <p className="activities-subtitle mb-0">
              {t("crm_activities_form_subtitle", {
                defaultValue:
                  "Plan the next real action, not just a log entry.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
          >
            {t("close", { defaultValue: "Close" })}
          </button>
        </div>
        {lookupsError && (
          <div className="alert alert-warning py-2 mb-3">{lookupsError}</div>
        )}
        <div className="row g-3">
          <div className="col-12 col-md-2">
            <label className="form-label">
              {t("crm_activities_type", { defaultValue: "Type" })}
            </label>
            <select
              className="form-select"
              data-academy={academyMode ? "activity-type" : undefined}
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
            >
              {ACTIVITY_TYPES.map((x) => (
                <option key={x} value={x}>
                  {getTypeLabel(x, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label">
              {t("crm_activities_action", { defaultValue: "Action" })}
            </label>
            <input
              className="form-control"
              data-academy={academyMode ? "activity-action" : undefined}
              value={form.subject}
              onChange={(e) =>
                setForm((p) => ({ ...p, subject: e.target.value }))
              }
              placeholder={t("crm_activities_action_placeholder", {
                defaultValue: "Example: Call Ivan about proposal",
              })}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">
              {t("crm_activities_when", { defaultValue: "When" })}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={form.when_at}
              onChange={(e) =>
                setForm((p) => ({ ...p, when_at: e.target.value }))
              }
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">
              {t("crm_activities_status", { defaultValue: "Status" })}
            </label>
            <select
              className="form-select"
              data-academy={academyMode ? "activity-status" : undefined}
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value }))
              }
            >
              {STATUSES.map((x) => (
                <option key={x} value={x}>
                  {getStatusLabel(x, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">
              {t("crm_activities_channel", { defaultValue: "Channel" })}
            </label>
            <select
              className="form-select"
              value={form.channel}
              onChange={(e) =>
                setForm((p) => ({ ...p, channel: e.target.value }))
              }
            >
              <option value="">—</option>
              {CHANNELS.map((x) => (
                <option key={x} value={x}>
                  {getChannelLabel(x, t)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">
              {t("crm_activities_direction", { defaultValue: "Direction" })}
            </label>
            <select
              className="form-select"
              value={form.direction}
              onChange={(e) =>
                setForm((p) => ({ ...p, direction: e.target.value }))
              }
            >
              <option value="">—</option>
              {DIRECTIONS.map((x) => (
                <option key={x} value={x}>
                  {getDirectionLabel(x, t)}
                </option>
              ))}
            </select>
          </div>
          <div
            className={`col-12 col-md-3 ${targetRequiredInvalid ? "activities-required-group" : ""}`}
          >
            <EntitySearchSelect
              label={t("crm_activities_contact", { defaultValue: "Contact" })}
              placeholder={t("crm_activities_select_contact", {
                defaultValue: "Select contact...",
              })}
              options={contactOptions}
              value={form.contact_ui_id}
              onChange={(value) =>
                setForm((p) => ({ ...p, contact_ui_id: value }))
              }
              loading={lookupsLoading}
              errorText={lookupsError}
              invalid={targetRequiredInvalid}
            />
          </div>
          <div
            className={`col-12 col-md-3 ${targetRequiredInvalid ? "activities-required-group" : ""}`}
          >
            <EntitySearchSelect
              label={t("crm_activities_lead", { defaultValue: "Lead" })}
              placeholder={t("crm_activities_select_lead", {
                defaultValue: "Select lead...",
              })}
              options={leadOptions}
              value={form.lead_ui_id}
              onChange={(value) =>
                setForm((p) => ({ ...p, lead_ui_id: value }))
              }
              loading={lookupsLoading}
              errorText={lookupsError}
              invalid={targetRequiredInvalid}
            />
          </div>
          <div
            className={`col-12 col-md-3 ${targetRequiredInvalid ? "activities-required-group" : ""}`}
          >
            <EntitySearchSelect
              label={t("crm_activities_deal", { defaultValue: "Deal" })}
              placeholder={t("crm_activities_select_deal", {
                defaultValue: "Select deal...",
              })}
              options={dealOptions}
              value={form.deal_ui_id}
              onChange={(value) =>
                setForm((p) => ({ ...p, deal_ui_id: value }))
              }
              loading={lookupsLoading}
              errorText={lookupsError}
              invalid={targetRequiredInvalid}
            />
          </div>
          <div className="col-12 col-md-9">
            <label className="form-label">
              {t("crm_activities_notes", { defaultValue: "Notes" })}
            </label>
            <textarea
              className="form-control"
              data-academy={academyMode ? "activity-notes" : undefined}
              rows={4}
              value={form.content}
              onChange={(e) =>
                setForm((p) => ({ ...p, content: e.target.value }))
              }
              placeholder={t("crm_activities_notes_placeholder", {
                defaultValue: "Context, promise, objection, next step...",
              })}
            />
          </div>
          {targetRequiredInvalid && (
            <div className="col-12">
              <div className="activities-field-error" role="alert">
                {t("crm_activities_related_target_required", {
                  defaultValue:
                    "Select at least one related target: Contact, Lead or Deal.",
                })}
              </div>
            </div>
          )}
          <div className="col-12 activities-form-actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={saving}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className="btn btn-success"
              data-academy={academyMode ? "activity-save" : undefined}
              disabled={saving}
            >
              {saving
                ? t("saving", { defaultValue: "Saving..." })
                : t("save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ActivityDetailsModal({
  activity,
  isBusy,
  onClose,
  onEdit,
  onDone,
  onCancel,
  onDelete,
}) {
  const { t } = useTranslation();
  const isPlanned = activity.status === "planned";
  const isTask = isTaskWorkItem(activity);
  const timelineTarget = useMemo(() => {
    if (isTask && (activity.__task_ui_id || activity.__task_id)) {
      return {
        type: "task",
        uiId: activity.__task_ui_id || activity.__task_id,
        label: t("crm_activities_timeline_for_task", {
          defaultValue: "Task timeline",
        }),
      };
    }
    if (activity.deal_ui_id) {
      return {
        type: "deal",
        uiId: activity.deal_ui_id,
        label: t("crm_activities_timeline_for_deal", {
          defaultValue: "Deal timeline",
        }),
      };
    }
    if (activity.lead_ui_id) {
      return {
        type: "lead",
        uiId: activity.lead_ui_id,
        label: t("crm_activities_timeline_for_lead", {
          defaultValue: "Lead timeline",
        }),
      };
    }
    if (activity.contact_ui_id) {
      return {
        type: "contact",
        uiId: activity.contact_ui_id,
        label: t("crm_activities_timeline_for_contact", {
          defaultValue: "Contact timeline",
        }),
      };
    }
    return null;
  }, [
    activity.__task_id,
    activity.__task_ui_id,
    activity.contact_ui_id,
    activity.deal_ui_id,
    activity.lead_ui_id,
    isTask,
    t,
  ]);

  const headerTitle = isTask
    ? t("crm_activities_task_number", {
        defaultValue: "Task #{{id}}",
        id: activity.__task_ui_id || activity.__task_id || activity.ui_id,
      })
    : t("crm_activities_activity_number", {
        defaultValue: "Activity #{{id}}",
        id: activity.ui_id,
      });

  const relationCards = [
    activity.contact_ui_id
      ? {
          label: t("crm_activities_contact", { defaultValue: "Contact" }),
          value:
            activity.contact_display_name ||
            t("crm_activities_contact_number", {
              defaultValue: "Contact #{{id}}",
              id: activity.contact_ui_id,
            }),
          meta: [activity.contact_primary_phone, activity.contact_primary_email]
            .filter(Boolean)
            .join(" · "),
        }
      : null,
    activity.lead_ui_id
      ? {
          label: t("crm_activities_lead", { defaultValue: "Lead" }),
          value:
            activity.lead_title ||
            t("crm_activities_lead_number", {
              defaultValue: "Lead #{{id}}",
              id: activity.lead_ui_id,
            }),
          meta: t("crm_activities_lead_number", {
            defaultValue: "Lead #{{id}}",
            id: activity.lead_ui_id,
          }),
        }
      : null,
    activity.deal_ui_id
      ? {
          label: t("crm_activities_deal", { defaultValue: "Deal" }),
          value:
            activity.deal_title ||
            t("crm_activities_deal_number", {
              defaultValue: "Deal #{{id}}",
              id: activity.deal_ui_id,
            }),
          meta: t("crm_activities_deal_number", {
            defaultValue: "Deal #{{id}}",
            id: activity.deal_ui_id,
          }),
        }
      : null,
  ].filter(Boolean);

  const taskFacts = [
    {
      label: t("crm_activities_status", { defaultValue: "Status" }),
      value: (
        <span className={`activities-status-pill ${activity.status}`}>
          {getStatusLabel(activity.status, t)}
        </span>
      ),
    },
    {
      label: t("crm_activities_due_at", { defaultValue: "Due date" }),
      value: formatDateTime(activity.when_at, t),
    },
    {
      label: t("crm_activities_owner", { defaultValue: "Owner" }),
      value:
        activity.owner_display_name ||
        activity.owner_email ||
        (activity.owner_ui_id
          ? t("crm_activities_owner_number", {
              defaultValue: "Owner #{{id}}",
              id: activity.owner_ui_id,
            })
          : "—"),
    },
    {
      label: t("crm_activities_created", { defaultValue: "Created" }),
      value: formatDateTime(activity.created_at, t),
    },
  ];

  const activityFacts = [
    {
      label: t("crm_activities_status", { defaultValue: "Status" }),
      value: (
        <span className={`activities-status-pill ${activity.status}`}>
          {getStatusLabel(activity.status, t)}
        </span>
      ),
    },
    {
      label: t("crm_activities_type", { defaultValue: "Type" }),
      value: activity.type ? getTypeLabel(activity.type, t) : "—",
    },
    {
      label: t("crm_activities_when", { defaultValue: "When" }),
      value: formatDateTime(activity.when_at, t),
    },
    activity.channel
      ? {
          label: t("crm_activities_channel", { defaultValue: "Channel" }),
          value: getChannelLabel(activity.channel, t),
        }
      : null,
    activity.direction
      ? {
          label: t("crm_activities_direction", { defaultValue: "Direction" }),
          value: getDirectionLabel(activity.direction, t),
        }
      : null,
    {
      label: t("crm_activities_owner", { defaultValue: "Owner" }),
      value:
        activity.owner_display_name ||
        activity.owner_email ||
        (activity.owner_ui_id
          ? t("crm_activities_owner_number", {
              defaultValue: "Owner #{{id}}",
              id: activity.owner_ui_id,
            })
          : "—"),
    },
    {
      label: t("crm_activities_created", { defaultValue: "Created" }),
      value: formatDateTime(activity.created_at, t),
    },
    activity.outcome
      ? {
          label: t("crm_activities_outcome", { defaultValue: "Outcome" }),
          value: activity.outcome,
        }
      : null,
  ].filter(Boolean);

  const facts = isTask ? taskFacts : activityFacts;

  return (
    <div className="activities-modal-backdrop" onMouseDown={onClose}>
      <div
        className="activities-modal activities-modal-wide"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="activities-form-header">
          <div>
            <h5 className="mb-1">
              {isTask ? "✅" : getActivityIcon(activity.type)} {headerTitle}
            </h5>
            <p className="activities-subtitle mb-0">
              {getActivityTitle(activity, t)}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
          >
            {t("close", { defaultValue: "Close" })}
          </button>
        </div>

        <div className="activities-detail-compact-panel">
          <div className="activities-detail-compact-head">
            <div>
              <div className="activities-detail-section-title mb-1">
                {isTask
                  ? t("crm_activities_work_item_summary", {
                      defaultValue: "Work item",
                    })
                  : t("crm_activities_activity_summary", {
                      defaultValue: "Activity summary",
                    })}
              </div>
              <div className="activities-detail-compact-title">
                {getActivityTitle(activity, t)}
              </div>
            </div>
            <div className="activities-detail-compact-id">{headerTitle}</div>
          </div>

          <div className="activities-detail-facts-grid">
            {facts.map((item) => (
              <DetailItem
                key={item.label}
                label={item.label}
                value={item.value}
              />
            ))}
          </div>

          {relationCards.length > 0 && (
            <div className="activities-related-strip">
              <div className="activities-detail-section-title">
                {t("crm_activities_related_chain", {
                  defaultValue: "Related CRM chain",
                })}
              </div>
              <div className="activities-related-pills">
                {relationCards.map((item) => (
                  <div className="activities-related-pill" key={item.label}>
                    <span className="activities-related-label">
                      {item.label}
                    </span>
                    <strong>{item.value}</strong>
                    {item.meta && <small>{item.meta}</small>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="activities-notes-compact">
            <div className="activities-detail-section-title">
              {isTask
                ? t("crm_activities_task_description", {
                    defaultValue: "Task description",
                  })
                : t("crm_activities_notes", { defaultValue: "Notes" })}
            </div>
            <div className="activities-notes-compact-text">
              {activity.content ||
                t("crm_activities_no_notes", { defaultValue: "No notes." })}
            </div>
          </div>
        </div>

        <div className="activities-detail-section">
          <CrmJourneyTimeline
            target={timelineTarget}
            title={t("crm_activities_timeline", { defaultValue: "Timeline" })}
            scopeLabel={timelineTarget?.label}
            limit={50}
          />
        </div>

        <div className="activities-form-actions mt-3">
          {isPlanned && (
            <button
              type="button"
              className="btn btn-success"
              disabled={isBusy}
              onClick={onDone}
            >
              {isBusy
                ? t("saving", { defaultValue: "Saving..." })
                : t("crm_activities_done", { defaultValue: "Done" })}
            </button>
          )}
          {isPlanned && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={isBusy}
              onClick={onCancel}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={isBusy}
            onClick={onEdit}
          >
            {t("edit", { defaultValue: "Edit" })}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger"
            disabled={isBusy}
            onClick={onDelete}
          >
            {t("delete", { defaultValue: "Delete" })}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="activities-detail-box activities-detail-box-compact">
      <div className="activities-detail-label">{label}</div>
      <div className="activities-detail-value">{value}</div>
    </div>
  );
}

function ActivityMeta({ activity }) {
  const { t } = useTranslation();

  return (
    <div className="activities-card-meta">
      <span>{formatDateTime(activity.when_at, t)}</span>
      <span>·</span>
      <span>
        {isTaskWorkItem(activity)
          ? t("crm_activities_task", { defaultValue: "Task" })
          : activity.type
            ? getTypeLabel(activity.type, t)
            : t("crm_activities_activity", { defaultValue: "activity" })}
      </span>
      {activity.channel && (
        <>
          <span>·</span>
          <span>{getChannelLabel(activity.channel, t)}</span>
        </>
      )}
      {activity.direction && (
        <>
          <span>·</span>
          <span>{getDirectionLabel(activity.direction, t)}</span>
        </>
      )}
    </div>
  );
}

function ActivityKanbanColumn({
  id,
  title,
  subtitle,
  tone,
  activityKeys,
  activityByKey,
  disabled,
  density,
  contactOptions,
  leadOptions,
  dealOptions,
  onOpen,
  onDone,
  onCancel,
  onEdit,
  onDelete,
  actionLoadingId,
  academyTaskId,
  academySearchGuideVisible,
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: String(id),
    disabled: false,
  });
  const isCompact = density === "compact";
  const academyColumnId = parseColumnId(id);

  return (
    <section
      data-academy={academyTaskId ? "activities-column" : undefined}
      data-academy-column={academyTaskId ? academyColumnId : undefined}
      className={`activities-kanban-column ${tone || ""} ${isOver ? "is-over" : ""}`}
    >
      <div className="activities-kanban-column-header">
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <span className="activities-section-count">{activityKeys.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={`activities-kanban-list ${isCompact ? "compact" : "comfortable"}`}
      >
        <SortableContext
          items={activityKeys}
          strategy={verticalListSortingStrategy}
        >
          {activityKeys.length === 0 && (
            <div className="activities-kanban-empty">
              {disabled
                ? "…"
                : t("crm_activities_drop_here", { defaultValue: "Drop here" })}
            </div>
          )}
          {activityKeys.map((key) => {
            const activity = activityByKey[String(key)];
            if (!activity) return null;
            return (
              <ActivityKanbanCard
                key={String(key)}
                id={String(key)}
                activity={activity}
                disabled={disabled}
                density={density}
                contactOptions={contactOptions}
                leadOptions={leadOptions}
                dealOptions={dealOptions}
                isBusy={actionLoadingId === activity.ui_id}
                isAcademyTask={
                  Boolean(academyTaskId) &&
                  String(activity.__task_id) === String(academyTaskId)
                }
                academySearchGuideVisible={academySearchGuideVisible}
                onOpen={() => onOpen(activity)}
                onDone={() => onDone(activity)}
                onCancel={() => onCancel(activity)}
                onEdit={() => onEdit(activity)}
                onDelete={() => onDelete(activity)}
              />
            );
          })}
        </SortableContext>
      </div>
    </section>
  );
}

function ActivityKanbanCard({
  id,
  activity,
  disabled,
  density,
  contactOptions,
  leadOptions,
  dealOptions,
  isBusy,
  isAcademyTask,
  academySearchGuideVisible,
  onOpen,
  onDone,
  onCancel,
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  const [hovered, setHovered] = useState(false);

  const isCompact = density === "compact";
  const showActions = density === "comfortable" || hovered;
  const isPlanned = activity.status === "planned";

  const compactRelationText = [
    activity.contact_ui_id ? `C:${activity.contact_ui_id}` : null,
    activity.lead_ui_id ? `L:${activity.lead_ui_id}` : null,
    activity.deal_ui_id ? `D:${activity.deal_ui_id}` : null,
  ]
    .filter(Boolean)
    .join(" • ");

  const detailItems = [
    {
      label: t("crm_activities_when", { defaultValue: "When" }),
      value: formatDateOnly(activity.when_at, t),
      title: formatDateTime(activity.when_at, t),
      group: "main",
    },
    {
      label: t("crm_activities_type", { defaultValue: "Type" }),
      value: activity.type ? getTypeLabel(activity.type, t) : "—",
      group: "main",
    },
    activity.channel
      ? {
          label: t("crm_activities_channel", { defaultValue: "Channel" }),
          value: getChannelLabel(activity.channel, t),
          group: "main",
        }
      : null,
    activity.direction
      ? {
          label: t("crm_activities_direction", { defaultValue: "Direction" }),
          value: getDirectionLabel(activity.direction, t),
          group: "main",
        }
      : null,
    activity.contact_ui_id
      ? {
          label: t("crm_activities_contact", { defaultValue: "Contact" }),
          value:
            getOptionTitleByUiId(contactOptions, activity.contact_ui_id) ||
            activity.contact_ui_id,
          group: "related",
        }
      : null,
    activity.lead_ui_id
      ? {
          label: t("crm_activities_lead", { defaultValue: "Lead" }),
          value:
            getOptionTitleByUiId(leadOptions, activity.lead_ui_id) ||
            activity.lead_ui_id,
          group: "related",
        }
      : null,
    activity.deal_ui_id
      ? {
          label: t("crm_activities_deal", { defaultValue: "Deal" }),
          value:
            getOptionTitleByUiId(dealOptions, activity.deal_ui_id) ||
            activity.deal_ui_id,
          group: "related",
        }
      : null,
  ].filter(Boolean);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    cursor: disabled ? "not-allowed" : "grab",
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClick = (e) => {
    if (isDragging) return;
    e.stopPropagation();
    onOpen();
  };

  return (
    <article
      ref={setNodeRef}
      data-academy={isAcademyTask ? "activities-task-card" : undefined}
      data-academy-entity-title={
        isAcademyTask ? activity.subject || "" : undefined
      }
      style={style}
      className={`activities-kanban-card status-${activity.status || "none"} ${isCompact ? "compact" : "comfortable"} ${
        isAcademyTask && academySearchGuideVisible
          ? "academyWorkflowTarget"
          : ""
      }`}
      {...(!disabled ? { ...attributes, ...listeners } : {})}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      title={
        disabled
          ? ""
          : t("crm_activities_card_title_hint", {
              defaultValue: "Click to view. Drag to move.",
            })
      }
    >
      {isCompact ? (
        <>
          <div className="activities-kanban-card-top">
            <div className="activities-kanban-card-icon">
              {getActivityIcon(activity.type)}
            </div>
            <div className="activities-kanban-card-main">
              <div
                className="activities-kanban-card-title"
                title={`#${activity.ui_id} ${getActivityTitle(activity, t)}`}
              >
                #{activity.ui_id} {getActivityTitle(activity, t)}
              </div>
              <div className="activities-kanban-card-meta">
                <span>
                  {isTaskWorkItem(activity)
                    ? t("crm_activities_task", { defaultValue: "Task" })
                    : activity.type
                      ? getTypeLabel(activity.type, t)
                      : t("crm_activities_activity", {
                          defaultValue: "activity",
                        })}
                </span>
                {activity.channel && (
                  <span>• {getChannelLabel(activity.channel, t)}</span>
                )}
                {activity.direction && (
                  <span>• {getDirectionLabel(activity.direction, t)}</span>
                )}
              </div>
              {compactRelationText && (
                <div className="activities-kanban-relations compact-relations">
                  {compactRelationText}
                </div>
              )}
            </div>
            <span className={`activities-status-pill ${activity.status}`}>
              {getStatusLabel(activity.status, t)}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="activities-card-comfort-head">
            <div className="activities-kanban-card-icon">
              {getActivityIcon(activity.type)}
            </div>
            <span className={`activities-status-pill ${activity.status}`}>
              {getStatusLabel(activity.status, t)}
            </span>
          </div>

          <div
            className="activities-card-comfort-title"
            title={`#${activity.ui_id} ${getActivityTitle(activity, t)}`}
          >
            #{activity.ui_id} {getActivityTitle(activity, t)}
          </div>

          <div className="activities-info-box">
            <div className="activities-info-title">
              {t("crm_activities_details", { defaultValue: "Details" })}
            </div>

            <div className="activities-info-grid">
              {detailItems.map((item, index) => (
                <div
                  className={`activities-info-item ${
                    item.group === "related" &&
                    detailItems[index - 1]?.group !== "related"
                      ? "activities-info-item-separated"
                      : ""
                  }`}
                  key={`${item.label}-${item.value}`}
                  title={item.title || `${item.label}: ${item.value}`}
                >
                  <span className="activities-info-label">{item.label}</span>
                  <span className="activities-info-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {activity.content && (
            <div className="activities-kanban-content">
              {String(activity.content).slice(0, 120)}
            </div>
          )}
        </>
      )}

      {showActions && (
        <div className="activities-kanban-actions">
          {isPlanned && (
            <button
              className="btn btn-success btn-sm"
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={(e) => {
                stop(e);
                onDone();
              }}
              disabled={disabled || isBusy}
              type="button"
            >
              {t("crm_activities_done", { defaultValue: "Done" })}
            </button>
          )}

          {isPlanned && (
            <button
              className="btn btn-outline-secondary btn-sm"
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={(e) => {
                stop(e);
                onCancel();
              }}
              disabled={disabled || isBusy}
              type="button"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
          )}

          <button
            className="btn btn-outline-primary btn-sm"
            onPointerDown={stop}
            onMouseDown={stop}
            onClick={(e) => {
              stop(e);
              onEdit();
            }}
            disabled={disabled || isBusy}
            type="button"
          >
            {t("edit", { defaultValue: "Edit" })}
          </button>

          <button
            className="btn btn-outline-danger btn-sm"
            onPointerDown={stop}
            onMouseDown={stop}
            onClick={(e) => {
              stop(e);
              onDelete();
            }}
            disabled={disabled || isBusy}
            type="button"
          >
            {t("delete", { defaultValue: "Delete" })}
          </button>
        </div>
      )}
    </article>
  );
}

function ActivityCardOverlay({ activity }) {
  const { t } = useTranslation();

  return (
    <div className="activities-kanban-card activities-kanban-overlay">
      <div className="activities-kanban-card-top">
        <div className="activities-kanban-card-icon">
          {getActivityIcon(activity.type)}
        </div>
        <div className="activities-kanban-card-main">
          <div className="activities-kanban-card-title">
            #{activity.ui_id} {getActivityTitle(activity, t)}
          </div>
          <ActivityMeta activity={activity} />
        </div>
      </div>
    </div>
  );
}
